"use client";

import {
  UserIcon,
  ChevronLeftIcon,
  CheckIcon,
  BookOpenIcon,
  CalendarIcon,
  ClockIcon,
  LoaderCircleIcon,
  UserPlusIcon,
  UsersIcon,
  LinkIcon,
  CheckCircle2Icon,
  SendIcon,
  ArrowRightIcon,
  XIcon,
  ShieldCheckIcon,
  ZapIcon,
  HeartIcon,
  FlameIcon,
  MapPinIcon,
  RotateCcwIcon,
  ExternalLinkIcon,
} from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";

import CoachAvatar from "#/components/shared/coach-avatar";
import { CoachDetailSheet } from "#/components/shared/coach-detail-sheet";
import WaiverBadge from "#/components/shared/waiver-badge";
import WaiverModal from "#/components/shared/waiver-modal";
import { Button } from "#/components/ui/button";
import { useAccentColor } from "#/hooks/use-accent-color";
import { useTimezone } from "#/hooks/use-timezone";
import { buildBookHref, readServiceParam } from "#/lib/booking-links";
import { explainEmptyMonthViaHorizon } from "#/lib/booking-window-display";
import { coachHasSheetContent } from "#/lib/coach-display";
import { coachesForService } from "#/lib/coaches-for-service";
import { formatPrice, formatDate, priceUnitLabel, bookingTotalInCents } from "#/lib/format";
import { formatLocalTime } from "#/lib/format-tz";
import { googleMapsUrl } from "#/lib/maps";
import { useOrg, useOrgHref } from "#/lib/org-context";
import type { MemberWaiverStatus } from "#/lib/waivers/waiver-status";
import type { WaiverStatus } from "#/lib/waivers/waiver-utils";
import { stepDateKey } from "#/utils/date";
import { latestDateOfBirth } from "#/validators/participant";
import { routePaths } from "#routes";

import { bookingCoachToSheetCoach } from "./booking-coach-adapter";
import { nextCoachSelection } from "./booking-coach-select";
import { nextBookingStep, type BookingStepTarget } from "./booking-next-step";
import type {
  Service,
  Program,
  Coach,
  Slot,
  Participant,
  WaiverStatusRecord,
  BookingPolicy,
} from "./booking-types";
import type { Phase } from "./booking-types";
import {
  StaggeredItem,
  StepIndicator,
  BookingSectionHeader as SectionHeader,
  SectionDivider,
  MonthCalendar,
  CoachProfileCard,
  SelectableCard,
} from "./booking-ui";

// ─── UI primitives imported from ./booking-ui ───

// ─── ExpandableBio ───
// Small inline component that clamps long instructor bios to two lines and
// reveals a Read more / Show less toggle when the text actually overflows.
//
// Earlier this component used a 140-char heuristic, which under-reported on
// mobile: bios in the 90–140 char range still wrap to 3+ lines inside the
// scoped-coach hero card on a phone, get hard-clamped, and the parent had
// no way to expand. Grace flagged exactly this on her live link.
//
// Strategy now: render the paragraph with `line-clamp-2`, then compare
// scrollHeight (full-content height) to clientHeight (visible clamped
// height). If the paragraph would be taller un-clamped, the text is truly
// being truncated and we surface the toggle. A ResizeObserver re-measures
// on viewport rotation, font load, and container reflow (e.g. when the
// desktop sidebar appears).
// ─── SummaryLocation ───
// Clear, legible location block shared by the Booking Summary (desktop
// sidebar) and the confirm-step summary (the surface a phone sees, since the
// sidebar is `hidden lg:block`). Renders the location name, the FULL
// (non-truncated) address, and an "Open in Google Maps" link generated from
// the address. Purely presentational off the slot's existing { name, address }
// — no new data fields. stopPropagation keeps the link from triggering any
// enclosing clickable card.
function SummaryLocation({
  location,
  accentColor,
}: {
  location: { name: string; address: string | null };
  accentColor: string;
}) {
  const mapsUrl = googleMapsUrl(location.address);
  return (
    <div className="flex items-start gap-2">
      <MapPinIcon
        className="h-4 w-4 shrink-0 mt-0.5"
        strokeWidth={1.8}
        style={{ color: "var(--bf-text-secondary)" }}
      />
      <div className="min-w-0">
        <p
          className="text-micro font-medium uppercase tracking-wider"
          style={{ color: "var(--bf-text-muted)" }}
        >
          Location
        </p>
        <p className="text-sm font-semibold break-words mt-0.5" style={{ color: "var(--bf-text)" }}>
          {location.name}
        </p>
        {location.address && (
          <p
            className="text-xs leading-snug break-words mt-0.5"
            style={{ color: "var(--bf-text-secondary)" }}
          >
            {location.address}
          </p>
        )}
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs font-semibold mt-1.5 hover:underline"
            style={{ color: accentColor }}
          >
            <ExternalLinkIcon className="h-3 w-3" />
            Open in Google Maps
          </a>
        )}
      </div>
    </div>
  );
}

function ExpandableBio({ bio, accentColor }: { bio: string; accentColor: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    function measure() {
      if (!el) return;
      // 2px fudge avoids sub-pixel false positives on retina displays.
      setOverflows(el.scrollHeight > el.clientHeight + 2);
    }
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, [bio, expanded]);

  const showToggle = overflows || expanded;

  return (
    <div className="mt-3">
      <p
        ref={ref}
        className={`text-md leading-relaxed ${expanded ? "" : "line-clamp-2"}`}
        style={{ color: "var(--bf-text-secondary)" }}
      >
        {bio}
      </p>
      {showToggle && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold py-1.5 -mx-1 px-1 rounded hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          style={{ color: accentColor }}
          aria-expanded={expanded}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
}

// The server already resolved each participant's status; narrow its two extra
// cases to what the badge and the gate understand. "requested" still needs
// signing, and a participant who owes nothing is clear to book.
function toWaiverStatuses(
  list: { id: string; waiverStatus?: MemberWaiverStatus }[],
): WaiverStatusRecord[] {
  return list.map((p) => {
    const status: WaiverStatus =
      p.waiverStatus === undefined || p.waiverStatus === "requested"
        ? "unsigned"
        : p.waiverStatus === "none_needed"
          ? "valid"
          : p.waiverStatus;
    return { participantId: p.id, status };
  });
}

function waiverStatusFor(participantId: string, statuses: WaiverStatusRecord[]): WaiverStatus {
  return statuses.find((w) => w.participantId === participantId)?.status ?? "unsigned";
}

// ─── Main ───

export default function BookingFlow() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const org = useOrg();
  const orgHref = useOrgHref();
  const timezone = useTimezone();
  const accentColor = useAccentColor();
  // Rebook mode: prefill service + coach + participant from a previous booking
  const rebookServiceId = searchParams.get("rebookServiceId");
  const rebookInstructorId = searchParams.get("rebookInstructorId");
  const rebookParticipantId = searchParams.get("rebookParticipantId");
  // Non-rebook participant preselection (e.g. from a parent-dashboard tile's
  // Book button). Kept separate from rebookParticipantId so it does NOT flip
  // the UI into rebook mode (isRebook below) — it just seeds the participant
  // chooser once phase === "who".
  const preselectParticipantId = searchParams.get("participantId");
  const isRebook = !!(rebookServiceId || rebookInstructorId);
  // When a STAFF user navigates here from the instructor dashboard, redirect back after booking
  const fromInstructor = searchParams.get("from") === "instructor";
  // Booking Event context: when a parent arrives from a public event landing
  const postBookingUrl = fromInstructor ? orgHref("/instructor") : orgHref("/dashboard");

  // Detect if the dashboard header is rendered by the book layout (authenticated user).
  // When present, we hide the booking flow's own nav bar to avoid duplicate headers.
  const [hasDashboardHeader, setHasDashboardHeader] = useState(false);
  useEffect(() => {
    setHasDashboardHeader(!!document.querySelector("[data-dashboard-header]"));
  }, []);

  // Coach-scoped mode: when a user arrives from a coach's direct storefront link (?cid=...)
  // This locks the experience to that coach rather than showing the multi-coach chooser.
  const [coachScopedMode, setCoachScopedMode] = useState(false);

  // Data
  const [programs, setPrograms] = useState<Program[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [waiverStatuses, setWaiverStatuses] = useState<WaiverStatusRecord[]>([]);
  const [policies, setPolicies] = useState<BookingPolicy[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Selections
  const [selectedCoachId, setSelectedCoachId] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
  const [loadingDates, setLoadingDates] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [, setSelectedSlot] = useState<Slot | null>(null);
  const [cart, setCart] = useState<(Slot & { date: string })[]>([]);
  const [cartExpanded, setCartExpanded] = useState(false);
  const [, setSelectedParticipant] = useState<Participant | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);
  const [policiesAcknowledged, setPoliciesAcknowledged] = useState(false);
  const [waiverModal, setWaiverModal] = useState<{
    participantId: string;
    participantName: string;
    participantDateOfBirth: string | null;
  } | null>(null);
  const [showQuickFill, setShowQuickFill] = useState(false);
  const [quickFillLoading, setQuickFillLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Which coach's profile sheet is open. Independent of `selectedCoachId`
  // so tapping "View profile" never changes which coach is selected.
  const [aboutCoachId, setAboutCoachId] = useState<string | null>(null);
  const [returningBannerDismissed, setReturningBannerDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(`cheeros_returning_dismissed_${org.slug}`) === "1";
  });

  // Booking flow config
  const [bookingMode, setBookingMode] = useState<"coach" | "date">("coach");
  const [allowRecurringBookings, setAllowRecurringBookings] = useState(true);
  const [minBookingNoticeHours, setMinBookingNoticeHours] = useState(0);
  const [bookingWindowDays, setBookingWindowDays] = useState(30);

  // Post-booking
  const [bookingShareUrl, setBookingShareUrl] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [spotsRemainingAfterBooking, setSpotsRemainingAfterBooking] = useState(0);

  // Phase type imported from ./booking-types
  const [phase, setPhase] = useState<Phase>("pick");
  const [phaseDirection, setPhaseDirection] = useState<"forward" | "back">("forward");
  const phaseOrder: Phase[] = ["pick", "who", "confirm", "done"];

  function navigatePhase(next: Phase) {
    const currentIdx = phaseOrder.indexOf(phase);
    const nextIdx = phaseOrder.indexOf(next);
    setPhaseDirection(nextIdx >= currentIdx ? "forward" : "back");
    setError("");
    setPhase(next);
  }

  // Inline participant form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newDob, setNewDob] = useState("");
  const [addingParticipant, setAddingParticipant] = useState(false);

  const allServices = programs.flatMap((p) => p.services);
  const selectedService = allServices.find((s) => s.id === selectedServiceId) || null;
  const isSemiPrivate = (selectedService?.maxParticipants ?? 1) > 1;
  const maxParticipants = selectedService?.maxParticipants ?? 1;
  const baseCartTotal = cart.reduce((sum, s) => sum + s.priceInCents, 0);
  // Semi-private multiplies the per-participant price by the participant count;
  // private is flat. Shared with the booking summary via bookingTotalInCents.
  const cartTotal = bookingTotalInCents(
    baseCartTotal,
    maxParticipants,
    selectedParticipants.length,
  );
  const singleCoach = coaches.length === 1;
  // Coach-scoped mode: user entered from a specific coach's storefront.
  // Show the locked coach hero instead of the multi-coach chooser.
  const scopedCoach = coachScopedMode ? coaches.find((c) => c.id === selectedCoachId) : null;
  const isDateFirstMode = bookingMode === "date" && !singleCoach && !scopedCoach && !isRebook;
  const showCoachChooser = !singleCoach && !scopedCoach && !isDateFirstMode;
  const singleService = allServices.length === 1;
  const showCalendar = !!(selectedServiceId || singleService);
  // Show the mode toggle when there are multiple coaches and no special mode
  const showModeToggle = !singleCoach && !scopedCoach && !isRebook && coaches.length > 1;

  const currentStep = phase === "pick" ? 0 : phase === "who" ? 1 : 2;

  const mostPopularServiceId = useMemo(
    () =>
      allServices.length >= 3
        ? allServices.reduce(
            (best, s) => (s.priceInCents < best.priceInCents ? s : best),
            allServices[0],
          )?.id
        : null,
    [allServices],
  );

  // ─── Init ───
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const safeFetch = (url: string): Promise<any> => {
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 10000);
      return fetch(url, { signal: ctrl.signal })
        .then((r) => {
          clearTimeout(tid);
          return r.json();
        })
        .catch(() => {
          clearTimeout(tid);
          return {};
        });
    };
    Promise.all([
      safeFetch(`/api/booking/filters?orgSlug=${encodeURIComponent(org.slug)}`),
      safeFetch("/api/auth/session"),
    ])
      .then(async ([f, session]) => {
        // Authed for THIS org iff the session's org matches this booking page's org.
        // On a shared domain a user can be logged into a different org than the page
        // they're viewing; that session can't book here, so treat it as anonymous.
        const sessionOrgId = session?.user?.organizationId ?? null;
        const authedForThisOrg = sessionOrgId != null && sessionOrgId === org.id;
        setIsAuthenticated(authedForThisOrg);
        setPrograms(f.programs || []);
        setAllowRecurringBookings(f.allowRecurringBookings ?? true);
        setMinBookingNoticeHours(
          typeof f.minBookingNoticeHours === "number" ? f.minBookingNoticeHours : 0,
        );
        setBookingWindowDays(typeof f.bookingWindowDays === "number" ? f.bookingWindowDays : 30);
        setCoaches(f.coaches || []);
        setPolicies(f.policies || []);
        // Only load participants when the session belongs to this org — a cross-org
        // or anonymous visitor has none to book with here. One call carries waiver
        // status folded in.
        if (authedForThisOrg) {
          const pData = await safeFetch("/api/rpc/participants");
          const list = pData.participants || [];
          setParticipants(list);
          setWaiverStatuses(toWaiverStatuses(list));
        }
        const svcs = (f.programs || []).flatMap((pr: Program) => pr.services);
        // Auto-select single coach (solo coach orgs)
        if ((f.coaches || []).length === 1) setSelectedCoachId(f.coaches[0].id);
        // Auto-select single service
        if (svcs.length === 1) setSelectedServiceId(svcs[0].id);

        // Restore booking state after login/SSO redirect.
        // Primary: localStorage (org-scoped, survives OAuth cross-origin hops).
        // Fallback: URL query params (sid/cid/date) in case localStorage was also cleared.
        let restored = false;
        try {
          const key = `cheeros_booking_${org.slug}`;
          const saved = localStorage.getItem(key);
          // Also clean up legacy sessionStorage key from older deploys
          try {
            sessionStorage.removeItem("cheeros_booking_state");
          } catch {}
          if (saved) {
            const state = JSON.parse(saved);
            localStorage.removeItem(key);
            // Only restore if saved within the last hour (ignore stale data)
            if (state.savedAt && Date.now() - state.savedAt < 3600000) {
              if (state.cart?.length > 0) setCart(state.cart);
              if (state.selectedServiceId) setSelectedServiceId(state.selectedServiceId);
              if (state.selectedCoachId) setSelectedCoachId(state.selectedCoachId);
              if (state.selectedDate) setSelectedDate(state.selectedDate);
              restored = true;
            }
          }
        } catch {}

        // Coach-scoped mode: detect ?cid= in URL regardless of localStorage restoration.
        // This is the signal that the user came from a coach's direct storefront.
        // Only activate if the coach actually exists in the loaded coaches list.
        const urlCid = new URLSearchParams(window.location.search).get("cid");
        const loadedCoaches: Coach[] = f.coaches || [];
        if (urlCid && loadedCoaches.some((c: Coach) => c.id === urlCid)) {
          setCoachScopedMode(true);
          // Ensure coach is selected even if localStorage restored a different state
          setSelectedCoachId(urlCid);
        } else if (urlCid) {
          // Invalid cid — fall through to normal multi-coach chooser
          setSelectedCoachId("");
        }

        // Fallback: restore remaining URL query params (no cart, but at least selections).
        // Service is read via readServiceParam so the canonical `sid` AND the
        // legacy/REST-style `serviceId` alias both pre-select — the storefront
        // service cards historically emitted `serviceId`, which the wizard ignored,
        // leaving the coach chooser unfiltered.
        if (!restored) {
          const sp = new URLSearchParams(window.location.search);
          const urlSid = readServiceParam((k) => sp.get(k));
          const urlDate = sp.get("date");
          if (urlSid) setSelectedServiceId(urlSid);
          if (urlDate) setSelectedDate(urlDate);
        }

        // Rebook mode: prefill service + coach from a previous booking.
        // These come from the real Enrollment → LessonSession → Service/Instructor chain.
        // CRITICAL: Only apply if the service/coach/participant still exist in the loaded data
        // AND are scoped to the current org. This prevents using prefill params from other orgs.
        const urlRebookSid = new URLSearchParams(window.location.search).get("rebookServiceId");
        const urlRebookIid = new URLSearchParams(window.location.search).get("rebookInstructorId");

        if (urlRebookSid) {
          const serviceExists = svcs.some((s: Service) => s.id === urlRebookSid);
          if (serviceExists) setSelectedServiceId(urlRebookSid);
        }
        if (urlRebookIid) {
          const coachExists = loadedCoaches.some((c: Coach) => c.id === urlRebookIid);
          if (coachExists) setSelectedCoachId(urlRebookIid);
        }

        setInitialLoading(false);
      })
      .catch(() => {
        setInitialLoading(false);
        setError("Failed to load booking data. Please refresh.");
      });
  }, [org.id, org.slug]);

  // ─── Load available dates ───
  const loadDates = useCallback(
    async (month: Date) => {
      if (!selectedServiceId) return;
      setLoadingDates(true);
      const monthStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
      const p = new URLSearchParams({ month: monthStr, orgSlug: org.slug });
      if (selectedServiceId) p.set("serviceId", selectedServiceId);
      if (selectedCoachId) p.set("instructorId", selectedCoachId);
      try {
        const r = await fetch(`/api/availability/dates?${p}`);
        const d = await r.json();
        setAvailableDates(new Set(d.dates || []));
      } catch {
        setAvailableDates(new Set());
      }
      setLoadingDates(false);
    },
    [selectedServiceId, selectedCoachId, org.slug],
  );

  useEffect(() => {
    if (showCalendar) {
      void loadDates(currentMonth);
      setSelectedDate("");
      setSelectedSlot(null);
      setSlots([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServiceId, selectedCoachId]);

  // ─── Auto-select participant when entering "who" phase ───
  // Accepts either rebookParticipantId (from Quick Rebook / rebook flow)
  // or participantId (from a parent-dashboard tile's Book button).
  useEffect(() => {
    const targetId = rebookParticipantId || preselectParticipantId;
    if (
      phase === "who" &&
      targetId &&
      participants.length > 0 &&
      selectedParticipants.length === 0
    ) {
      const match = participants.find((p) => p.id === targetId);
      if (match) {
        setSelectedParticipants([match]);
        setSelectedParticipant(match);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, participants, rebookParticipantId, preselectParticipantId]);

  // ─── Date selection ───
  async function pickDate(date: string) {
    setSelectedDate(date);
    setSelectedSlot(null);
    setSlotsLoading(true);
    scrollToBookingStep("time", { mobileOnly: true });
    const p = new URLSearchParams({ date, orgSlug: org.slug });
    if (selectedServiceId) p.set("serviceId", selectedServiceId);
    if (selectedCoachId) p.set("instructorId", selectedCoachId);
    try {
      const r = await fetch(`/api/availability?${p}`);
      const d = await r.json();
      setSlots(d.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }

  // ─── Auto-select the only available date ───
  // When a month resolves to a single bookable day, pick it for the user so
  // they don't have to click the lone highlighted date. Gated on !selectedDate
  // so it fires once per load and never overrides a manual pick.
  useEffect(() => {
    if (showCalendar && !loadingDates && !selectedDate && availableDates.size === 1) {
      void pickDate([...availableDates][0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCalendar, loadingDates, availableDates, selectedDate]);

  // ─── Cart helpers ───
  function slotKey(slot: Slot, date: string) {
    return `${slot.instructorId}-${slot.serviceId || ""}-${date}-${slot.startTime}`;
  }

  // Recurring allowed = org master switch AND selected coach allows it
  const selectedCoach = coaches.find((c) => c.id === selectedCoachId);
  const recurringAllowed =
    allowRecurringBookings && selectedCoach?.allowRecurringBookings !== false;

  function toggleSlotInCart(slot: Slot) {
    const date = selectedDate;
    const key = slotKey(slot, date);
    const existing = cart.findIndex((s) => slotKey(s, s.date) === key);
    if (existing >= 0) {
      setCart((prev) => prev.filter((_, i) => i !== existing));
      setShowQuickFill(false);
    } else {
      const entry = { ...slot, date };
      setCart((prev) => [...prev, entry]);
      setSelectedSlot(slot);
      if (cart.length === 0 && recurringAllowed) setShowQuickFill(true);
      else setShowQuickFill(false);
    }
  }

  function removeFromCart(index: number) {
    setCart((prev) => prev.filter((_, i) => i !== index));
  }

  async function quickFillWeeks(weeks: number) {
    if (cart.length === 0 || !selectedServiceId) return;
    setQuickFillLoading(true);
    setError("");
    const first = cart[0];
    const newEntries: (Slot & { date: string })[] = [];

    // Check availability for each future week in parallel
    const weekPromises: Promise<{
      week: number;
      ds: string;
      available: boolean;
      slot: Slot | null;
    }>[] = [];
    for (let w = 1; w < weeks; w++) {
      const ds = stepDateKey(first.date, w * 7);
      if (cart.some((s) => s.date === ds)) continue;

      weekPromises.push(
        fetch(
          `/api/availability?date=${ds}&serviceId=${selectedServiceId}&instructorId=${first.instructorId}&orgSlug=${org.slug}`,
        )
          .then((r) => r.json())
          .then((data) => {
            // Find a matching slot from the same instructor at the same local time.
            // We can't just shift UTC ms because DST transitions change the offset.
            // Instead, extract the HH:MM from the original slot and match by time string.
            const originalHHMM = new Date(first.startTime).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            });
            const matchingSlot = (data.slots || []).find((s: Slot) => {
              if (s.instructorId !== first.instructorId) return false;
              const slotHHMM = new Date(s.startTime).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              });
              return slotHHMM === originalHHMM;
            });
            return { week: w, ds, available: !!matchingSlot, slot: matchingSlot ?? null };
          })
          .catch(() => ({ week: w, ds, available: false, slot: null })),
      );
    }

    const results = await Promise.all(weekPromises);
    for (const { ds, available, slot: matchedSlot } of results) {
      if (!available || !matchedSlot) continue;
      if (cart.some((s) => s.date === ds)) continue;
      // Use the exact startTime/endTime from the availability API (DST-correct)
      newEntries.push({
        ...first,
        date: ds,
        startTime: matchedSlot.startTime,
        endTime: matchedSlot.endTime,
      });
    }

    if (newEntries.length === 0) {
      setError(
        "No matching availability found for future weeks. The instructor may not have availability at this time every week.",
      );
      setQuickFillLoading(false);
      setShowQuickFill(false);
      return;
    }
    setCart((prev) => [...prev, ...newEntries]);
    setQuickFillLoading(false);
    setShowQuickFill(false);
  }

  function continueFromCart() {
    if (cart.length === 0) return;
    if (!selectedServiceId && cart[0].serviceId) setSelectedServiceId(cart[0].serviceId);
    if (!selectedCoachId) setSelectedCoachId(cart[0].instructorId);
    // If user has no participants loaded and none were fetched, they're likely unauthenticated
    // Check by making a lightweight auth probe — if participants array is empty AND
    // the initial fetch returned nothing, prompt login
    if (participants.length === 0 && !isAuthenticated) {
      // Persist booking state so the cart survives the login/SSO redirect.
      // Use localStorage (not sessionStorage) because sessionStorage is unreliable
      // across Google OAuth redirects — some browsers clear it during cross-origin hops.
      const bookingState = {
        cart,
        selectedServiceId,
        selectedCoachId,
        selectedDate,
        savedAt: Date.now(),
      };
      try {
        localStorage.setItem(`cheeros_booking_${org.slug}`, JSON.stringify(bookingState));
      } catch {}
      // Also encode essential IDs in the callbackUrl as a belt-and-suspenders fallback.
      // Same canonical param contract the storefront/coach links use (sid/cid/date).
      const bookPath = orgHref(
        buildBookHref({
          serviceId: selectedServiceId,
          coachId: selectedCoachId,
          date: selectedDate,
        }),
      );
      const callbackUrl = encodeURIComponent(bookPath);
      router.push(orgHref(`/login?callbackUrl=${callbackUrl}`));
      return;
    }
    navigatePhase("who");
  }

  // ─── Participant selection ───
  async function pickParticipant(p: Participant) {
    // For semi-private: toggle selection
    if (isSemiPrivate) {
      setSelectedParticipants((prev) => {
        const exists = prev.find((sp) => sp.id === p.id);
        if (exists) return prev.filter((sp) => sp.id !== p.id);
        const slot = cart[0];
        const spotsFilled = slot?.spotsFilled ?? 0;
        const remaining = maxParticipants - spotsFilled;
        if (prev.length >= remaining) {
          setError(`This session has ${remaining} spot${remaining !== 1 ? "s" : ""} remaining.`);
          return prev;
        }
        setError("");
        return [...prev, p];
      });
      setSelectedParticipant(p);
      return;
    }
    // For private: select participant (even if waiver needed)
    setSelectedParticipant(p);
    setSelectedParticipants([p]);
  }

  async function continueFromWho(freshWaiverStatuses?: typeof waiverStatuses) {
    if (selectedParticipants.length === 0) return;

    // Use fresh data if provided (from onSigned callback), otherwise fall back to state
    const statuses = freshWaiverStatuses ?? waiverStatuses;

    // Check if any selected participant needs waiver
    const needsWaiver = selectedParticipants.some((p) => {
      const st = waiverStatusFor(p.id, statuses);
      return st === "unsigned" || st === "expired";
    });

    if (needsWaiver) {
      // Open waiver modal for the first participant that needs it
      const p = selectedParticipants.find((p) => {
        const st = waiverStatusFor(p.id, statuses);
        return st === "unsigned" || st === "expired";
      })!;
      setWaiverModal({
        participantId: p.id,
        participantName: `${p.firstName} ${p.lastName}`,
        participantDateOfBirth: p.dateOfBirth,
      });
      return;
    }

    setSelectedParticipant(selectedParticipants[0]);
    navigatePhase("confirm");
  }

  async function rollbackBookingGroup(bookingGroupId: string, reason: string) {
    try {
      await fetch("/api/bookings/group-cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookingGroupId, reason }),
      });
    } catch {
      // Best effort rollback for multi-step client booking flows.
    }
  }

  // ─── Book ───
  const confirmingRef = useRef(false);
  // Count of slots dropped to a 409 "slot taken" conflict in a partial
  // multi-slot booking. Carried to the post-booking redirect so the parent is
  // told how many slots were dropped rather than silently seeing fewer bookings.
  const droppedConflictsRef = useRef(0);

  // ─── Next-step scrolling ───
  // Refs on the three booking sections so the post-instructor "Choose …" CTAs
  // (inline panel, desktop summary, mobile footer) and selection events can
  // smooth-scroll the user to the next unfilled step. Each target carries
  // scroll-mt to clear the sticky nav + stepper.
  const serviceSectionRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const slotsRef = useRef<HTMLDivElement>(null);
  function scrollToBookingStep(target: BookingStepTarget, opts?: { mobileOnly?: boolean }) {
    // Auto-advance on selection is an iPhone affordance; on desktop the sticky
    // summary already guides and an auto-scroll would fight the wider layout.
    if (
      opts?.mobileOnly &&
      typeof window !== "undefined" &&
      !window.matchMedia("(max-width: 1023px)").matches
    )
      return;
    const run = () => {
      const el =
        target === "service"
          ? serviceSectionRef.current
          : target === "date"
            ? calendarRef.current
            : slotsRef.current;
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // Defer two frames so a section that first mounts as a result of the same
    // tap (calendar after a service pick, slots after a date pick) is committed.
    requestAnimationFrame(() => requestAnimationFrame(run));
  }

  async function handleConfirm() {
    if (confirmingRef.current) return; // Prevent double-click before React re-renders
    if (!selectedServiceId || selectedParticipants.length === 0 || cart.length === 0) {
      setError("Please select a service, participant, and time slot before booking.");
      return;
    }
    // Lessons that must be paid online are booked and paid in the app's booking flow, which
    // holds the lesson while the family pays. This page only hands them over.
    const coach = coaches.find((c) => c.id === cart[0].instructorId);
    if (coach?.onlinePaymentRequired) {
      const slot = cart[0];
      const locationId = slot.locations[0]?.id;
      if (cart.length > 1 || selectedParticipants.length > 1 || !locationId) {
        setError(
          `Lessons with ${coach.name ?? "this coach"} are paid when you book, one lesson at a time. Keep one lesson and one participant, then continue.`,
        );
        return;
      }
      confirmingRef.current = true;
      window.location.assign(
        orgHref(
          routePaths.spaBook({
            participantId: selectedParticipants[0].id,
            date: slot.date,
            slot: {
              coachId: slot.instructorId,
              serviceId: selectedServiceId,
              locationId,
              start: slot.startTime,
            },
          }),
        ),
      );
      return;
    }
    confirmingRef.current = true;
    setSubmitting(true);
    setError("");
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 30000);
    try {
      const firstSlot = cart[0];
      const primaryParticipant = selectedParticipants[0];
      const additionalParticipants = selectedParticipants.slice(1);

      if (cart.length === 1 && firstSlot.existingSessionId) {
        let joinSuccess = 0;
        let joinError = "";
        const joinBookingGroupId =
          selectedParticipants.length > 1 ? crypto.randomUUID() : undefined;
        for (const p of selectedParticipants) {
          const res = await fetch("/api/bookings/join/by-session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: ctrl.signal,
            body: JSON.stringify({
              sessionId: firstSlot.existingSessionId,
              participantId: p.id,
              policiesAcknowledged,
              ...(joinBookingGroupId ? { bookingGroupId: joinBookingGroupId } : {}),
            }),
          });
          const data = await res.json();
          if (res.ok) joinSuccess++;
          else joinError = data.error || "Booking failed.";
        }
        if (joinSuccess === 0) {
          setError(joinError || "Booking failed.");
          return;
        }
        if (joinSuccess !== selectedParticipants.length) {
          if (joinBookingGroupId) {
            await rollbackBookingGroup(
              joinBookingGroupId,
              "Partial existing-session multi-participant join failed",
            );
          }
          setError(joinError || "We couldn't add every participant, so no bookings were kept.");
          return;
        }

        router.push(`${postBookingUrl}?booked=true`);
        return;
      }

      let successCount = 0;
      let lastError = "";
      let lastShareUrl: string | null = null;
      let lastBookingData: Record<string, string | null> | null = null;
      // Track 409 "slot taken" failures so we can recover the parent to the
      // slot picker with a clear message instead of a dead-end generic error.
      const conflictedSlots: typeof cart = [];
      let hadSlotConflict = false;
      droppedConflictsRef.current = 0;

      // Recurring series path: send all dates in one request to create a RecurringSeries
      const isRecurringSeries = cart.length > 1 && recurringAllowed && !isSemiPrivate;
      const bookingGroupId =
        !isRecurringSeries && (cart.length > 1 || additionalParticipants.length > 0)
          ? crypto.randomUUID()
          : undefined;

      if (isRecurringSeries) {
        const recurringDates = cart.map((slot) => ({
          startTime: slot.startTime,
          endTime: slot.endTime,
        }));
        const body: Record<string, unknown> = {
          serviceId: selectedServiceId,
          participantId: primaryParticipant.id,
          instructorId: firstSlot.instructorId,
          startTime: firstSlot.startTime,
          endTime: firstSlot.endTime,
          locationId: firstSlot.locations[0]?.id || undefined,
          policiesAcknowledged,
          recurring: {
            frequency: "weekly",
            dates: recurringDates,
          },
        };
        try {
          const res = await fetch("/api/bookings", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: ctrl.signal,
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (res.ok) {
            const sessions = data.series?.sessions ?? data.sessions ?? [];
            successCount = sessions.length || cart.length;
          } else if (res.status === 401) {
            setError("Your session has expired. Please log in again.");
            setSubmitting(false);
            router.push(orgHref("/login"));
            return;
          } else if (res.status === 409) {
            hadSlotConflict = true;
            lastError =
              data.error ||
              "That time was taken before checkout was completed. Please choose another time.";
          } else {
            lastError = data.error || `Booking failed (${res.status})`;
          }
        } catch (e) {
          lastError = e instanceof Error ? e.message : "Network error";
        }
      } else {
        // Single booking or semi-private: individual requests
        for (const slot of cart) {
          const body: Record<string, unknown> = {
            serviceId: selectedServiceId,
            participantId: primaryParticipant.id,
            instructorId: slot.instructorId,
            startTime: slot.startTime,
            endTime: slot.endTime,
            locationId: slot.locations[0]?.id || undefined,
            policiesAcknowledged,
          };
          if (bookingGroupId) body.bookingGroupId = bookingGroupId;
          try {
            const res = await fetch("/api/bookings", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              signal: ctrl.signal,
              body: JSON.stringify(body),
            });
            const data = await res.json();
            if (res.ok) {
              successCount++;
              if (data.shareUrl) lastShareUrl = data.shareUrl;
              // Capture booking details for the success banner (only once for the first booking)
              if (!lastBookingData) {
                const service = allServices.find((s) => s.id === selectedServiceId);
                const coach = coaches.find((c) => c.id === slot.instructorId);
                if (service && coach && primaryParticipant) {
                  lastBookingData = {
                    serviceId: selectedServiceId,
                    serviceName: service.name,
                    instructorId: slot.instructorId,
                    instructorName: coach.name || null,
                    participantId: primaryParticipant.id,
                    participantFirstName: primaryParticipant.firstName,
                  };
                }
              }
              if (additionalParticipants.length > 0 && data.sessionId) {
                let joinFailed = false;
                let joinFailureMessage = "";
                for (const ap of additionalParticipants) {
                  try {
                    const joinRes = await fetch("/api/bookings/join/by-session", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      signal: ctrl.signal,
                      body: JSON.stringify({
                        sessionId: data.sessionId,
                        participantId: ap.id,
                        policiesAcknowledged,
                        ...(bookingGroupId ? { bookingGroupId: bookingGroupId } : {}),
                      }),
                    });
                    const joinData = await joinRes.json();
                    if (!joinRes.ok) {
                      joinFailed = true;
                      joinFailureMessage =
                        joinData.error || "Failed to add one or more participants.";
                      break;
                    }
                  } catch {
                    joinFailed = true;
                    joinFailureMessage = "Failed to add one or more participants.";
                    break;
                  }
                }
                if (joinFailed) {
                  if (bookingGroupId) {
                    await rollbackBookingGroup(
                      bookingGroupId,
                      "Partial semi-private multi-participant booking failed",
                    );
                  }
                  setError(
                    joinFailureMessage ||
                      "We couldn't add every participant, so no bookings were kept.",
                  );
                  return;
                }
              }
            } else if (res.status === 401) {
              setError("Your session has expired. Please log in again.");
              setSubmitting(false);
              router.push(orgHref("/login"));
              return;
            } else if (res.status === 409) {
              hadSlotConflict = true;
              conflictedSlots.push(slot);
              lastError =
                data.error ||
                "That time was taken before checkout was completed. Please choose another time.";
            } else {
              lastError = data.error || `Booking failed (${res.status})`;
            }
          } catch (e) {
            lastError = e instanceof Error ? e.message : "Network error";
          }
        }
      }
      if (successCount === 0) {
        // A taken slot (409) recovers the parent to slot selection with a clear,
        // actionable message — never a dead-end error, never a success UI.
        if (hadSlotConflict) {
          if (conflictedSlots.length > 0) {
            const taken = conflictedSlots;
            setCart((prev) =>
              prev.filter(
                (c) =>
                  !taken.some(
                    (cs) => cs.startTime === c.startTime && cs.instructorId === c.instructorId,
                  ),
              ),
            );
          }
          // navigatePhase() clears `error`, so set the recovery message AFTER it —
          // otherwise the parent returns to slot selection with no explanation.
          navigatePhase("pick");
          setError(
            "That time was taken before checkout was completed. Please choose another time.",
          );
          return;
        }
        setError(lastError || "All bookings failed. Please try again.");
        return;
      }

      // Partial conflict — some slots committed, some were taken before checkout.
      // Remove the taken ones from the cart and carry a count to the post-booking
      // surface so the parent is told rather than silently seeing fewer bookings.
      if (conflictedSlots.length > 0) {
        const taken = conflictedSlots;
        droppedConflictsRef.current = taken.length;
        setCart((prev) =>
          prev.filter(
            (c) =>
              !taken.some(
                (cs) => cs.startTime === c.startTime && cs.instructorId === c.instructorId,
              ),
          ),
        );
      }

      if (isSemiPrivate && lastShareUrl) {
        setBookingShareUrl(lastShareUrl);
        setSpotsRemainingAfterBooking(
          maxParticipants - selectedParticipants.length - (cart[0]?.spotsFilled ?? 0),
        );
        navigatePhase("done");
        return;
      }

      // Build redirect URL with explicit booking data to avoid generic "recent booking" issues
      const redirectParams = new URLSearchParams({
        booked: "true",
        seriesCount: String(successCount),
      });
      if (lastBookingData) {
        redirectParams.set("bookingData", encodeURIComponent(JSON.stringify(lastBookingData)));
      }
      if (droppedConflictsRef.current > 0) {
        redirectParams.set("conflicts", String(droppedConflictsRef.current));
      }
      router.push(`${postBookingUrl}?${redirectParams.toString()}`);
    } catch (err) {
      setError(
        err instanceof DOMException && err.name === "AbortError"
          ? "Request timed out."
          : "Something went wrong.",
      );
    } finally {
      clearTimeout(tid);
      setSubmitting(false);
      confirmingRef.current = false;
    }
  }

  // ─── Loading (Skeleton) ───
  if (initialLoading)
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundColor: "var(--bf-bg)",
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        }}
      >
        <nav
          className="sticky top-0 z-50 bg-white/85 backdrop-blur-2xl"
          style={{ borderBottom: "1px solid", borderBottomColor: "var(--bf-border)" }}
        >
          <div
            className="max-w-5xl mx-auto px-5 sm:px-6 flex items-center justify-between"
            style={{ height: 52 }}
          >
            <div className="skeleton-row w-20" style={{ height: 14 }} />
            <div className="skeleton-row w-28" style={{ height: 14 }} />
            <div className="w-16" />
          </div>
        </nav>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 space-y-6 animate-in fade-in duration-500">
          <div className="flex items-center justify-center gap-3 py-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                {i > 0 && (
                  <div
                    className="w-10 sm:w-14 h-px"
                    style={{ backgroundColor: "var(--bf-border)" }}
                  />
                )}
                <div
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: "var(--bf-border)" }}
                />
              </div>
            ))}
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="skeleton-row w-9 h-9 rounded-lg" />
              <div className="space-y-1.5">
                <div className="skeleton-row w-32" />
                <div className="skeleton-row w-24" style={{ height: 12 }} />
              </div>
            </div>
            <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="skeleton-card h-[88px]"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </div>
          </div>
          <div className="h-px" style={{ backgroundColor: "var(--bf-border)" }} />
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="skeleton-row w-9 h-9 rounded-lg" />
              <div className="space-y-1.5">
                <div className="skeleton-row w-28" />
                <div className="skeleton-row w-36" style={{ height: 12 }} />
              </div>
            </div>
            <div className="grid gap-2.5 sm:grid-cols-2">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="skeleton-card h-[100px]"
                  style={{ animationDelay: `${i * 150 + 300}ms` }}
                />
              ))}
            </div>
          </div>
          <div className="h-px" style={{ backgroundColor: "var(--bf-border)" }} />
          <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
            <div className="skeleton-card h-[300px]" style={{ animationDelay: "600ms" }} />
            <div
              className="skeleton-card h-[300px] hidden lg:block"
              style={{ animationDelay: "750ms" }}
            />
          </div>
        </div>
      </div>
    );

  if (allServices.length === 0) {
    const isDarkEmpty = false; // booking flow runs in dashboard context (always light for now)
    return (
      <div
        className="min-h-[50vh] flex items-center justify-center px-4"
        style={{ backgroundColor: isDarkEmpty ? "#0C0C0D" : "var(--bf-bg)" }}
      >
        <div className="max-w-sm w-full text-center">
          <div
            className="h-14 w-14 rounded-xl mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: "var(--bf-bg-elevated)" }}
          >
            <BookOpenIcon
              className="h-6 w-6"
              strokeWidth={1.5}
              style={{ color: "var(--bf-text-muted)" }}
            />
          </div>
          <p
            className="font-semibold text-md tracking-[-0.02em]"
            style={{ color: "var(--bf-text)" }}
          >
            No services available
          </p>
          <p className="text-md mt-2 leading-relaxed" style={{ color: "var(--bf-text-secondary)" }}>
            This instructor hasn&apos;t added any services yet. Check back soon.
          </p>
        </div>
      </div>
    );
  }

  const filteredServices = selectedCoachId
    ? allServices.filter((s) =>
        coaches.find((c) => c.id === selectedCoachId)?.serviceIds.includes(s.id),
      )
    : allServices;
  // Instructors eligible for the chosen service (all of them until one is picked).
  // Drives the chooser grid, its empty state, and the header count so they agree.
  const eligibleCoaches = coachesForService(coaches, selectedServiceId);
  const isDark = false; // booking flow runs in dashboard context (always light for now)

  // After an instructor is explicitly picked in the multi-coach chooser the
  // page otherwise looks static (the card just flips to "Selected"). These
  // drive the next-step CTAs that walk the user through the remaining steps in
  // order — a service first (multi-service orgs), then a date, then a time.
  // showCalendar is true once a service is chosen or auto-selected (single-
  // service orgs), so it doubles as the "service step satisfied" flag.
  const instructorExplicitlyChosen = showCoachChooser && !!selectedCoachId;
  const nextStep = nextBookingStep({ serviceChosen: showCalendar, dateChosen: !!selectedDate });

  return (
    <div
      className="min-h-screen"
      data-storefront-theme="light"
      style={
        {
          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
          backgroundColor: isDark ? "#0C0C0D" : "var(--bf-bg)",
          "--bf-bg": isDark ? "#0C0C0D" : "#F8F8F6",
          "--bf-bg-card": isDark ? "#161618" : "#FFFFFF",
          "--bf-bg-elevated": isDark ? "#1E1E21" : "#F0F0EE",
          "--bf-text": isDark ? "#F5F5F5" : "#1A1A1A",
          "--bf-text-secondary": isDark ? "#A1A1AA" : "#6B6B6B",
          "--bf-text-muted": isDark ? "#71717A" : "#9B9B9B",
          "--bf-text-disabled": isDark ? "#52525B" : "#C5C5C2",
          "--bf-border": isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        } as React.CSSProperties
      }
    >
      {/* Navigation Bar — hidden when dashboard header is present (authenticated user) */}
      {!hasDashboardHeader && (
        <nav
          className="sticky top-0 z-50 backdrop-blur-2xl border-b"
          style={{
            backgroundColor: isDark ? "rgba(22,22,24,0.85)" : "rgba(255,255,255,0.85)",
            borderColor: "var(--bf-border)",
          }}
        >
          <div
            className="max-w-5xl mx-auto px-5 sm:px-6 h-13 flex items-center justify-between"
            style={{ height: 52 }}
          >
            <Link
              href={scopedCoach?.slug ? orgHref(`/coach/${scopedCoach.slug}`) : orgHref("/")}
              className="flex items-center gap-1.5 text-sm font-medium transition-colors"
              style={{ color: "var(--bf-text-secondary)" }}
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{scopedCoach ? scopedCoach.name : org.name}</span>
            </Link>
            <span
              className="text-sm font-semibold tracking-[-0.01em]"
              style={{ color: "var(--bf-text)" }}
            >
              {scopedCoach
                ? `Book with ${scopedCoach.name?.split(" ")[0] || "Instructor"}`
                : "Book a Session"}
            </span>
            {!isAuthenticated ? (
              <Link
                href={orgHref(`/login?callbackUrl=${encodeURIComponent(orgHref("/book"))}`)}
                className="text-tiny font-medium transition-colors px-3 py-1.5 rounded-xl"
                style={{ color: accentColor }}
              >
                Sign In
              </Link>
            ) : (
              <div className="w-16" />
            )}
          </div>
        </nav>
      )}

      <div
        className="sticky top-[52px] z-40 backdrop-blur-xl border-b"
        style={{
          backgroundColor: isDark ? "rgba(12,12,13,0.9)" : "rgba(249,250,251,0.9)",
          borderColor: "var(--bf-border)",
        }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <StepIndicator current={currentStep} accentColor={accentColor} />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-44 lg:pb-8">
        {error && (
          <div
            role="alert"
            className="rounded-xl bg-red-50 border border-red-200/60 p-3 text-sm text-red-600 flex items-center gap-2.5 mb-5 animate-in fade-in slide-in-from-top-1 duration-300"
          >
            <XIcon className="h-4 w-4 shrink-0" strokeWidth={2} />
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Returning customer banner — shown to unauthenticated users on the pick phase */}
        {!isAuthenticated && !returningBannerDismissed && phase === "pick" && !isRebook && (
          <div
            className="rounded-xl border p-3 mb-5 flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-1 duration-300"
            style={{
              backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
              borderColor: "var(--bf-border)",
            }}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <UserIcon className="h-4 w-4 shrink-0" style={{ color: accentColor }} />
              <span className="text-sm" style={{ color: "var(--bf-text)" }}>
                Been here before?{" "}
                <Link
                  href={orgHref(`/login?callbackUrl=${encodeURIComponent(orgHref("/book"))}`)}
                  className="font-semibold underline underline-offset-2"
                  style={{ color: accentColor }}
                >
                  Sign in
                </Link>{" "}
                to book faster with your saved info.
              </span>
            </div>
            <button
              onClick={() => {
                setReturningBannerDismissed(true);
                localStorage.setItem(`cheeros_returning_dismissed_${org.slug}`, "1");
              }}
              className="shrink-0 p-1 rounded-lg transition-colors hover:bg-black/5"
              aria-label="Dismiss"
            >
              <XIcon className="h-3.5 w-3.5" style={{ color: "var(--bf-text-muted)" }} />
            </button>
          </div>
        )}

        {/* Rebook banner */}
        {isRebook && phase === "pick" && (
          <div
            className="rounded-xl border p-3 mb-5 flex items-center gap-2.5 animate-in fade-in duration-300"
            style={{ backgroundColor: `${accentColor}08`, borderColor: `${accentColor}25` }}
          >
            <RotateCcwIcon className="h-4 w-4 shrink-0" style={{ color: accentColor }} />
            <span className="text-sm font-medium" style={{ color: "var(--bf-text)" }}>
              Booking again — your instructor and service are preselected. Just pick a new date and
              time.
            </span>
          </div>
        )}

        {/* ═══════ PICK PHASE ═══════ */}
        {phase === "pick" && (
          <div
            className={`lg:grid lg:grid-cols-[1fr_340px] lg:gap-6 pb-28 lg:pb-0 ${phaseDirection === "forward" ? "phase-enter-forward" : "phase-enter-back"}`}
            key="pick"
          >
            {/* ─── Left column: main content ─── */}
            <div className="space-y-7">
              {/* Booking mode toggle: Browse by Coach / Browse by Date */}
              {showModeToggle && (
                <div
                  className="flex items-center gap-1 p-1 rounded-xl w-fit"
                  style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                >
                  <button
                    onClick={() => {
                      setBookingMode("coach");
                      setSelectedSlot(null);
                      setSlots([]);
                      setSelectedDate("");
                      setCart([]);
                      setShowQuickFill(false);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${bookingMode === "coach" ? "" : "hover:bg-black/[0.045]"}`}
                    style={
                      bookingMode === "coach"
                        ? {
                            backgroundColor: "var(--bf-bg-card)",
                            color: "var(--bf-text)",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          }
                        : { color: "var(--bf-text)" }
                    }
                  >
                    <span className="flex items-center gap-1.5">
                      <UserIcon className="h-3.5 w-3.5" />
                      Browse by Instructor
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setBookingMode("date");
                      setSelectedCoachId("");
                      setSelectedSlot(null);
                      setSlots([]);
                      setSelectedDate("");
                      setCart([]);
                      setShowQuickFill(false);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${bookingMode === "date" ? "" : "hover:bg-black/[0.045]"}`}
                    style={
                      bookingMode === "date"
                        ? {
                            backgroundColor: "var(--bf-bg-card)",
                            color: "var(--bf-text)",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          }
                        : { color: "var(--bf-text)" }
                    }
                  >
                    <span className="flex items-center gap-1.5">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      Browse by Date
                    </span>
                  </button>
                </div>
              )}

              {/* Date-first mode hint */}
              {isDateFirstMode && selectedServiceId && (
                <div
                  className="rounded-xl border p-3 flex items-center gap-2.5 animate-in fade-in duration-300"
                  style={{ backgroundColor: `${accentColor}08`, borderColor: `${accentColor}25` }}
                >
                  <CalendarIcon className="h-4 w-4 shrink-0" style={{ color: accentColor }} />
                  <span className="text-sm" style={{ color: "var(--bf-text)" }}>
                    Showing availability across{" "}
                    {coaches.length === 1 ? "your instructor" : `all ${coaches.length} instructors`}
                    . Pick a time to continue.
                  </span>
                </div>
              )}

              {/* Coach-scoped hero: locked coach from direct storefront link.
                Suppressed when singleCoach is also true — the standard coach card below renders the same info. */}
              {scopedCoach && !singleCoach && (
                <StaggeredItem index={0}>
                  <div
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: "var(--bf-bg-card)",
                      border: "1px solid var(--bf-border)",
                    }}
                  >
                    <div
                      className="h-14 relative"
                      style={{
                        background: `linear-gradient(135deg, ${accentColor}15 0%, ${accentColor}05 100%)`,
                      }}
                    />
                    <div className="px-5 pb-5 -mt-5 relative">
                      <div className="flex items-end gap-3.5">
                        <div
                          className="shrink-0 rounded-full p-[2px] ring-2 ring-white shadow-sm"
                          style={{ backgroundColor: "var(--bf-bg-card)" }}
                        >
                          <CoachAvatar
                            name={scopedCoach.name}
                            image={scopedCoach.image}
                            size={52}
                            accentColor={accentColor}
                          />
                        </div>
                        <div className="flex-1 min-w-0 pb-0.5">
                          <div className="flex items-center gap-2 flex-nowrap min-w-0">
                            <h2
                              className="font-semibold text-lg tracking-[-0.02em] truncate"
                              style={{ color: "var(--bf-text)" }}
                            >
                              Booking with {scopedCoach.name || "Coach"}
                            </h2>
                          </div>
                        </div>
                      </div>

                      {scopedCoach.bio && (
                        <ExpandableBio bio={scopedCoach.bio} accentColor={accentColor} />
                      )}

                      <div
                        className="flex items-center justify-between mt-3 pt-3"
                        style={{ borderTop: "1px solid var(--bf-border)" }}
                      >
                        <div className="flex items-center flex-wrap gap-x-5 gap-y-1.5">
                          {[
                            {
                              icon: BookOpenIcon,
                              label: `${filteredServices.length} service${filteredServices.length !== 1 ? "s" : ""}`,
                            },
                            { icon: ZapIcon, label: "Instant booking" },
                          ].map(({ icon: I, label }) => (
                            <span
                              key={label}
                              className="text-xs flex items-center gap-1.5 font-medium"
                              style={{ color: "var(--bf-text-secondary)" }}
                            >
                              <I
                                className="h-3.5 w-3.5"
                                style={{ color: "var(--bf-text-muted)" }}
                                strokeWidth={1.8}
                              />
                              {label}
                            </span>
                          ))}
                        </div>
                        <button
                          onClick={() => {
                            setCoachScopedMode(false);
                            setSelectedCoachId("");
                            setSelectedSlot(null);
                            setSelectedDate("");
                            setSlots([]);
                          }}
                          className="text-xs font-medium transition-colors hover:underline"
                          style={{ color: "var(--bf-text-muted)" }}
                        >
                          View all instructors
                        </button>
                      </div>
                    </div>
                  </div>
                </StaggeredItem>
              )}

              {/* Multi-coach chooser: shown when NOT in coach-scoped mode and org has multiple coaches */}
              {showCoachChooser && (
                <div>
                  <SectionHeader
                    icon={UserIcon}
                    title="Choose Your Instructor"
                    subtitle={`${eligibleCoaches.length} instructor${eligibleCoaches.length === 1 ? "" : "s"} available`}
                  />

                  {/* Helper nudge for families browsing for the first time —
                    only visible when no specific coach is selected, so it
                    doesn't nag once the user has picked. */}
                  {!selectedCoachId && (
                    <p className="text-tiny mb-3 -mt-1" style={{ color: "var(--bf-text-muted)" }}>
                      Not sure who to choose? Leave All Instructors selected and pick the time that
                      works best.
                    </p>
                  )}

                  {/* Featured full-width "All Instructors" — pulled out of the
                    coach grid so it never steals a grid slot (that made the
                    individual coach row wrap unevenly, e.g. Lauren alone with
                    Grace on a lonely second row). */}
                  <StaggeredItem index={0}>
                    <SelectableCard
                      isSelected={!selectedCoachId}
                      accentColor={accentColor}
                      onClick={() => {
                        setSelectedCoachId("");
                        setSelectedSlot(null);
                        setSelectedDate("");
                        setSlots([]);
                      }}
                      className="mb-3"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="h-11 w-11 rounded-full flex items-center justify-center shrink-0"
                          style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                        >
                          <UsersIcon
                            className="h-5 w-5"
                            strokeWidth={1.8}
                            style={{ color: "var(--bf-text-secondary)" }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="font-semibold text-md tracking-[-0.01em]"
                            style={{ color: "var(--bf-text)" }}
                          >
                            All Instructors
                          </p>
                          <p
                            className="text-md mt-0.5"
                            style={{ color: "var(--bf-text-secondary)" }}
                          >
                            See all {coaches.length} instructors
                          </p>
                        </div>
                        {!selectedCoachId && (
                          <div
                            className="h-5 w-5 rounded-full flex items-center justify-center text-white shrink-0"
                            style={{ backgroundColor: accentColor }}
                          >
                            <CheckIcon className="h-3 w-3" strokeWidth={3} />
                          </div>
                        )}
                      </div>
                    </SelectableCard>
                  </StaggeredItem>

                  {selectedServiceId && eligibleCoaches.length === 0 ? (
                    <StaggeredItem index={1}>
                      <div
                        className="flex flex-col items-center justify-center py-8 text-center rounded-xl"
                        style={{
                          backgroundColor: "var(--bf-bg-card)",
                          border: "1.5px dashed var(--bf-border)",
                        }}
                      >
                        <UserIcon
                          className="h-6 w-6 mb-2"
                          strokeWidth={1.2}
                          style={{ color: "var(--bf-text-disabled)" }}
                        />
                        <p
                          className="text-sm font-semibold"
                          style={{ color: "var(--bf-text-secondary)" }}
                        >
                          No instructors teach this service yet
                        </p>
                        <p className="text-tiny mt-1" style={{ color: "var(--bf-text-muted)" }}>
                          Try selecting a different service
                        </p>
                      </div>
                    </StaggeredItem>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {eligibleCoaches.map((c, i) => (
                        <CoachProfileCard
                          key={c.id}
                          coach={c}
                          services={allServices}
                          accentColor={accentColor}
                          isSelected={selectedCoachId === c.id}
                          onSelect={() => {
                            setSelectedCoachId((prev) => nextCoachSelection(prev, c.id));
                            setSelectedSlot(null);
                            setSelectedDate("");
                            setSlots([]);
                          }}
                          onAbout={() => setAboutCoachId(c.id)}
                          hasSheetContent={coachHasSheetContent({
                            bio: c.bio,
                            specialties: c.specialties ?? [],
                            extendedBio: c.extendedBio,
                            instagramUrl: c.instagramUrl,
                            tiktokUrl: c.tiktokUrl,
                            facebookUrl: c.facebookUrl,
                          })}
                          index={i}
                        />
                      ))}
                    </div>
                  )}

                  {/* Inline next-step CTA — appears right under the coach grid
                    once a specific instructor is picked, so the flow no longer
                    looks static. Lives in the always-visible left column, so it
                    guides desktop and mobile alike. Context-aware: points to a
                    service first (multi-service orgs), then date & time. */}
                  {instructorExplicitlyChosen && cart.length === 0 && (
                    <div
                      className="mt-4 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
                      style={{
                        backgroundColor: `${accentColor}08`,
                        border: `1.5px solid ${accentColor}25`,
                      }}
                    >
                      <div
                        className="h-9 w-9 rounded-full flex items-center justify-center text-white shrink-0"
                        style={{ backgroundColor: accentColor }}
                      >
                        <CheckIcon className="h-4 w-4" strokeWidth={3} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="font-semibold text-md tracking-[-0.01em]"
                          style={{ color: "var(--bf-text)" }}
                        >
                          {selectedCoach?.name || "Instructor"} selected
                        </p>
                        <p className="text-sm mt-0.5" style={{ color: "var(--bf-text-secondary)" }}>
                          {nextStep.headline}
                        </p>
                      </div>
                      <Button
                        onClick={() => scrollToBookingStep(nextStep.target)}
                        className="w-full sm:w-auto h-10 px-4 text-white font-semibold text-sm gap-1.5 rounded-lg transition-all duration-200 hover:opacity-90 active:scale-[0.98] shrink-0"
                        style={{ backgroundColor: accentColor }}
                      >
                        {nextStep.ctaLabel}
                        <ArrowRightIcon className="h-4 w-4" strokeWidth={2} />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Solo coach hero */}
              {singleCoach && (
                <StaggeredItem index={0}>
                  <div
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: "var(--bf-bg-card)",
                      border: "1px solid var(--bf-border)",
                    }}
                  >
                    <div
                      className="h-14 relative"
                      style={{
                        background: `linear-gradient(135deg, ${accentColor}15 0%, ${accentColor}05 100%)`,
                      }}
                    />
                    <div className="px-5 pb-5 -mt-5 relative">
                      <div className="flex items-end gap-3.5">
                        <div
                          className="shrink-0 rounded-full p-[2px] ring-2 ring-white shadow-sm"
                          style={{ backgroundColor: "var(--bf-bg-card)" }}
                        >
                          <CoachAvatar
                            name={coaches[0].name}
                            image={coaches[0].image}
                            size={52}
                            accentColor={accentColor}
                          />
                        </div>
                        <div className="flex-1 min-w-0 pb-0.5">
                          <div className="flex items-center gap-2 flex-nowrap min-w-0">
                            <h2
                              className="font-semibold text-lg tracking-[-0.02em] truncate"
                              style={{ color: "var(--bf-text)" }}
                            >
                              {coaches[0].name || "Your Instructor"}
                            </h2>
                            <span
                              className="flex items-center gap-0.5 text-2xs font-semibold px-1.5 py-[2px] rounded text-white"
                              style={{ backgroundColor: accentColor }}
                            >
                              <ShieldCheckIcon className="h-2.5 w-2.5" />
                              Verified
                            </span>
                          </div>
                        </div>
                      </div>

                      {coaches[0].bio && (
                        <ExpandableBio bio={coaches[0].bio} accentColor={accentColor} />
                      )}

                      <div
                        className="flex items-center flex-wrap gap-x-5 gap-y-1.5 mt-3 pt-3"
                        style={{ borderTop: "1px solid var(--bf-border)" }}
                      >
                        {[
                          {
                            icon: BookOpenIcon,
                            label: `${allServices.length} service${allServices.length !== 1 ? "s" : ""}`,
                          },
                          { icon: ZapIcon, label: "Instant booking" },
                          { icon: HeartIcon, label: "Trusted" },
                        ].map(({ icon: I, label }) => (
                          <span
                            key={label}
                            className="text-xs flex items-center gap-1.5 font-medium"
                            style={{ color: "var(--bf-text-secondary)" }}
                          >
                            <I
                              className="h-3.5 w-3.5"
                              style={{ color: "var(--bf-text-muted)" }}
                              strokeWidth={1.8}
                            />
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </StaggeredItem>
              )}

              {!singleService && <SectionDivider />}

              {/* Service Cards */}
              {!singleService && (
                <div ref={serviceSectionRef} className="scroll-mt-32">
                  <SectionHeader
                    icon={BookOpenIcon}
                    title="Select a Service"
                    subtitle="Choose the type of session"
                  />

                  {(selectedCoachId ? filteredServices : allServices).length === 0 && (
                    <div
                      className="flex flex-col items-center justify-center py-10 text-center rounded-xl"
                      style={{
                        backgroundColor: "var(--bf-bg-card)",
                        border: "1.5px dashed var(--bf-border)",
                      }}
                    >
                      <BookOpenIcon
                        className="h-7 w-7 mb-3"
                        strokeWidth={1.2}
                        style={{ color: "var(--bf-text-disabled)" }}
                      />
                      <p
                        className="text-md font-semibold"
                        style={{ color: "var(--bf-text-secondary)" }}
                      >
                        No services available
                      </p>
                      <p
                        className="text-tiny mt-1 max-w-xs mx-auto"
                        style={{ color: "var(--bf-text-muted)" }}
                      >
                        {selectedCoachId
                          ? "This instructor doesn\u2019t offer any services yet. Try selecting a different instructor."
                          : "No services are available for booking right now. Check back soon!"}
                      </p>
                    </div>
                  )}

                  <div
                    className={`grid gap-2.5 ${(selectedCoachId ? filteredServices : allServices).length === 1 ? "" : "sm:grid-cols-2 lg:grid-cols-3"}`}
                  >
                    {(selectedCoachId ? filteredServices : allServices).map((s, sIdx) => {
                      const isSelected = selectedServiceId === s.id;
                      const isSP = (s.maxParticipants ?? 1) > 1;
                      const isPopular = s.id === mostPopularServiceId;
                      const activeCoach = selectedCoachId
                        ? coaches.find((c) => c.id === selectedCoachId)
                        : null;
                      // Use instructor-specific price when a coach is selected, fall back to base service price.
                      const displayPrice = activeCoach?.servicePrices?.[s.id] ?? s.priceInCents;
                      return (
                        <StaggeredItem key={s.id} index={sIdx}>
                          <button
                            type="button"
                            aria-pressed={isSelected}
                            aria-label={isSelected ? `${s.name} selected` : `Select ${s.name}`}
                            onClick={() => {
                              setSelectedServiceId(s.id);
                              setSelectedSlot(null);
                              setSelectedDate("");
                              setSlots([]);
                              scrollToBookingStep("date", { mobileOnly: true });
                            }}
                            className="group relative w-full min-w-0 rounded-xl text-left transition-all duration-200 overflow-hidden h-full flex flex-col cursor-pointer hover:-translate-y-0.5"
                            style={
                              isSelected
                                ? {
                                    backgroundColor: `${accentColor}06`,
                                    border: `2px solid ${accentColor}`,
                                    boxShadow: `0 0 0 3px ${accentColor}10`,
                                  }
                                : {
                                    backgroundColor: "var(--bf-bg-card)",
                                    border: "1.5px solid var(--bf-border)",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                  }
                            }
                          >
                            {/* Hover affordance — accent border that fades in on hover
                              when the card isn't selected. Separate element so it
                              doesn't fight the inline base border. */}
                            {!isSelected && (
                              <div
                                className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                style={{ border: `2px solid ${accentColor}66` }}
                              />
                            )}
                            {/* Top accent strip when selected */}
                            <div
                              className="h-1 w-full rounded-t-xl transition-all duration-200"
                              style={{ backgroundColor: isSelected ? accentColor : "transparent" }}
                            />

                            <div className="p-4 flex flex-col flex-1">
                              {/* Popular badge — above price row to prevent overlap */}
                              {isPopular && (
                                <div className="mb-1.5">
                                  <span
                                    className="inline-flex items-center gap-0.5 text-2xs font-semibold px-2 py-[3px] rounded-full text-white"
                                    style={{ backgroundColor: accentColor }}
                                  >
                                    <FlameIcon className="h-2.5 w-2.5" />
                                    Popular
                                  </span>
                                </div>
                              )}

                              {/* Header: price + selected check */}
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex flex-col">
                                  <span
                                    className="text-xl font-bold tracking-[-0.03em]"
                                    style={{ color: isSelected ? accentColor : "var(--bf-text)" }}
                                  >
                                    {formatPrice(displayPrice)}
                                  </span>
                                  {/* Per-unit label so parents see at a glance whether the price is
                                    per participant (semi-private) or a flat lesson price (private). */}
                                  {displayPrice > 0 && (
                                    <span
                                      className="text-2xs font-medium"
                                      style={{ color: "var(--bf-text-muted)" }}
                                    >
                                      {priceUnitLabel(s.maxParticipants ?? 1)}
                                    </span>
                                  )}
                                </div>
                                {isSelected && (
                                  <span
                                    className="h-6 w-6 rounded-full flex items-center justify-center text-white shrink-0"
                                    style={{ backgroundColor: accentColor }}
                                  >
                                    <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
                                  </span>
                                )}
                              </div>

                              {/* Title — `break-words` prevents long service
                                names from running off the card edge on
                                narrow desktop grid cells (Grace's report). */}
                              <p
                                className="font-semibold text-md tracking-[-0.01em] break-words"
                                style={{ color: "var(--bf-text)" }}
                              >
                                {s.name}
                              </p>

                              {/* Meta pills */}
                              <div className="flex items-center flex-wrap gap-1.5 mt-2">
                                <span
                                  className="inline-flex items-center gap-1 text-sm font-medium px-2 py-[3px] rounded-full"
                                  style={{
                                    backgroundColor: "var(--bf-bg-elevated)",
                                    color: "var(--bf-text-secondary)",
                                  }}
                                >
                                  <ClockIcon className="h-3 w-3" />
                                  {s.durationMinutes} min
                                </span>
                                {isSP && (
                                  <span
                                    className="inline-flex items-center gap-1 text-sm font-medium px-2 py-[3px] rounded-full"
                                    style={{
                                      backgroundColor: "var(--bf-bg-elevated)",
                                      color: "var(--bf-text-secondary)",
                                    }}
                                  >
                                    <UsersIcon className="h-3 w-3" />
                                    Semi-private
                                  </span>
                                )}
                              </div>

                              {/* Description */}
                              {s.description && (
                                <p
                                  className="text-tiny mt-2.5 leading-relaxed line-clamp-2 flex-1"
                                  style={{ color: "var(--bf-text-secondary)" }}
                                >
                                  {s.description}
                                </p>
                              )}

                              {/* Explicit Select / Selected CTA. Rendered as a
                                styled span (not a nested <button>) since the
                                whole card is already the click target — this
                                just makes the affordance obvious (Sahil
                                couldn't tell the cards were clickable). */}
                              <div className="mt-auto pt-3">
                                <span
                                  className="flex items-center justify-center gap-1.5 w-full rounded-lg py-2 text-sm font-semibold transition-colors group-hover:brightness-95"
                                  style={
                                    isSelected
                                      ? { backgroundColor: accentColor, color: "#fff" }
                                      : {
                                          border: `1.5px solid ${accentColor}`,
                                          color: accentColor,
                                          backgroundColor: `${accentColor}0A`,
                                        }
                                  }
                                >
                                  {isSelected ? (
                                    <>
                                      <CheckIcon className="h-4 w-4" strokeWidth={3} />
                                      Selected
                                    </>
                                  ) : (
                                    "Select"
                                  )}
                                </span>
                              </div>
                            </div>
                          </button>
                        </StaggeredItem>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Single service banner */}
              {singleService &&
                allServices[0] &&
                (() => {
                  return (
                    <StaggeredItem index={0}>
                      <div
                        className="rounded-2xl p-4 flex items-center justify-between"
                        style={{
                          backgroundColor: "var(--bf-bg-card)",
                          border: "1px solid var(--bf-border)",
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="h-9 w-9 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                          >
                            <BookOpenIcon
                              className="h-[17px] w-[17px]"
                              strokeWidth={1.8}
                              style={{ color: "var(--bf-text-secondary)" }}
                            />
                          </div>
                          <div>
                            <p
                              className="font-semibold text-md tracking-[-0.01em]"
                              style={{ color: "var(--bf-text)" }}
                            >
                              {allServices[0].name}
                            </p>
                            <p
                              className="text-md flex items-center gap-1 mt-0.5"
                              style={{ color: "var(--bf-text-secondary)" }}
                            >
                              <ClockIcon className="h-3 w-3" />
                              {allServices[0].durationMinutes} min
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <span
                            className="text-lg font-semibold tracking-[-0.02em]"
                            style={{ color: accentColor }}
                          >
                            {formatPrice(
                              selectedCoach?.servicePrices?.[allServices[0].id] ??
                                allServices[0].priceInCents,
                            )}
                          </span>
                          {(selectedCoach?.servicePrices?.[allServices[0].id] ??
                            allServices[0].priceInCents) > 0 && (
                            <span
                              className="text-2xs font-medium"
                              style={{ color: "var(--bf-text-muted)" }}
                            >
                              {priceUnitLabel(allServices[0].maxParticipants ?? 1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </StaggeredItem>
                  );
                })()}

              {/* Calendar prompt */}
              {!showCalendar && (
                <div
                  className="rounded-2xl border border-dashed py-14 text-center"
                  style={{
                    borderColor: "var(--bf-text-disabled)",
                    backgroundColor: "var(--bf-bg-card)",
                  }}
                >
                  <div
                    className="h-12 w-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                    style={{ backgroundColor: "var(--bf-bg)" }}
                  >
                    <CalendarIcon
                      className="h-5 w-5"
                      strokeWidth={1.5}
                      style={{ color: "var(--bf-text-disabled)" }}
                    />
                  </div>
                  <p
                    className="text-md font-semibold tracking-[-0.01em]"
                    style={{ color: "var(--bf-text-secondary)" }}
                  >
                    Select a service to see available times
                  </p>
                  <p className="text-md mt-1" style={{ color: "var(--bf-text-muted)" }}>
                    Calendar opens after you choose a service
                  </p>
                </div>
              )}

              {showCalendar && <SectionDivider />}

              {/* Calendar + Slots */}
              {showCalendar && (
                <div ref={calendarRef} className="scroll-mt-32">
                  <SectionHeader
                    icon={CalendarIcon}
                    title="Pick a Date & Time"
                    subtitle="Choose when works best"
                  />

                  <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
                    <div
                      className="rounded-2xl p-4"
                      style={{
                        backgroundColor: "var(--bf-bg-card)",
                        border: "1px solid var(--bf-border)",
                      }}
                    >
                      <MonthCalendar
                        availableDates={availableDates}
                        selectedDate={selectedDate}
                        onSelectDate={pickDate}
                        currentMonth={currentMonth}
                        onMonthChange={(dir) => {
                          const n = new Date(currentMonth);
                          n.setMonth(n.getMonth() + dir);
                          setCurrentMonth(n);
                          void loadDates(n);
                        }}
                        loadingDates={loadingDates}
                        accentColor={accentColor}
                      />
                      {!loadingDates &&
                        availableDates.size === 0 &&
                        (() => {
                          // Today in the ORG timezone so the boundary agrees with the
                          // server's booking-window math, not the viewer's clock.
                          const todayYmd = new Intl.DateTimeFormat("en-CA", {
                            timeZone: timezone,
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          }).format(new Date());
                          const horizon = explainEmptyMonthViaHorizon({
                            displayedYear: currentMonth.getFullYear(),
                            displayedMonth: currentMonth.getMonth() + 1,
                            todayYmd,
                            bookingWindowDays,
                          });
                          return (
                            <div className="flex flex-col items-center justify-center py-6 text-center">
                              {horizon ? (
                                <>
                                  <p
                                    className="text-sm font-medium"
                                    style={{ color: "var(--bf-text-muted)" }}
                                  >
                                    Online booking opens {bookingWindowDays} day
                                    {bookingWindowDays !== 1 ? "s" : ""} in advance.
                                  </p>
                                  {/* T00:00:00 keeps formatDate on the intended calendar day (bare
                                  YYYY-MM-DD would parse as UTC midnight and can render a day off). */}
                                  <p
                                    className="text-sm mt-1"
                                    style={{ color: "var(--bf-text-disabled)" }}
                                  >
                                    Dates after {formatDate(`${horizon.horizonEndYmd}T00:00:00`)}{" "}
                                    aren&apos;t open yet — check back closer to the date.
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p
                                    className="text-sm font-medium"
                                    style={{ color: "var(--bf-text-muted)" }}
                                  >
                                    No available dates for this selection.
                                  </p>
                                  <p
                                    className="text-sm mt-1"
                                    style={{ color: "var(--bf-text-disabled)" }}
                                  >
                                    {isDateFirstMode
                                      ? "Try a different service or check back later."
                                      : "Try a different instructor or service."}
                                  </p>
                                </>
                              )}
                            </div>
                          );
                        })()}
                    </div>

                    <div ref={slotsRef} className="scroll-mt-32">
                      {!selectedDate && (
                        <div className="flex flex-col items-center justify-center py-10 sm:py-14 text-center">
                          <CalendarIcon
                            className="h-7 w-7 mb-3"
                            strokeWidth={1.2}
                            style={{ color: "var(--bf-text-disabled)" }}
                          />
                          <p
                            className="text-md font-semibold tracking-[-0.01em]"
                            style={{ color: "var(--bf-text-secondary)" }}
                          >
                            Pick a date
                          </p>
                          <p className="text-md mt-1" style={{ color: "var(--bf-text-secondary)" }}>
                            Highlighted dates have availability
                          </p>
                        </div>
                      )}

                      {selectedDate && minBookingNoticeHours > 0 && (
                        <p
                          className="text-xs mb-2 px-0.5"
                          style={{ color: "var(--bf-text-secondary)" }}
                        >
                          Bookings must be made at least {minBookingNoticeHours} hour
                          {minBookingNoticeHours !== 1 ? "s" : ""} before the lesson start time.
                        </p>
                      )}

                      {selectedDate && slotsLoading && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 auto-rows-fr">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                              <div
                                key={i}
                                className="rounded-xl p-4 animate-pulse"
                                style={{
                                  backgroundColor: "var(--bf-bg-card)",
                                  border: "2px solid var(--bf-border)",
                                }}
                              >
                                <div className="h-5 w-12 bg-muted rounded mb-2" />
                                <div className="h-4 w-8 bg-muted rounded mb-4" />
                                <div className="h-4 w-20 bg-muted rounded" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedDate && !slotsLoading && slots.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-10 sm:py-14 text-center">
                          <CalendarIcon
                            className="h-7 w-7 mb-3"
                            strokeWidth={1.2}
                            style={{ color: "var(--bf-text-disabled)" }}
                          />
                          <p
                            className="text-md font-semibold"
                            style={{ color: "var(--bf-text-secondary)" }}
                          >
                            No times available
                          </p>
                          <p className="text-md mt-1" style={{ color: "var(--bf-text-secondary)" }}>
                            {isDateFirstMode
                              ? "No instructors have openings on this date. Try another day."
                              : "Try a different date"}
                          </p>
                        </div>
                      )}

                      {selectedDate && !slotsLoading && slots.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between px-0.5">
                            <p
                              className="text-md font-semibold tracking-[-0.01em]"
                              style={{ color: "var(--bf-text)" }}
                            >
                              {formatDate(selectedDate + "T12:00:00")}
                            </p>
                            <span
                              className="text-xs font-medium px-2.5 py-1 rounded-full"
                              style={{
                                backgroundColor: "var(--bf-bg-elevated)",
                                color: "var(--bf-text-secondary)",
                              }}
                            >
                              {slots.length} time{slots.length !== 1 ? "s" : ""} available
                            </span>
                          </div>
                          {/* Slot grid caps at 2 cols — a 3rd column against the 340px calendar +
                            340px sidebar leaves cards too narrow (price/badge wrap). */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 auto-rows-fr">
                            {slots.map((slot, i) => {
                              const key = slotKey(slot, selectedDate);
                              const inCart = cart.some((s) => slotKey(s, s.date) === key);
                              const slotTz = slot.locations[0]?.timezone ?? timezone;
                              const startLabel = formatLocalTime(slot.startTime, slotTz);
                              const endLabel = formatLocalTime(slot.endTime, slotTz);
                              // Drop the start meridiem when it matches the end so the range fits
                              // one line ("1:30 – 2:00 PM"); keep both when they straddle noon.
                              const startLabelShort =
                                startLabel.slice(-2) === endLabel.slice(-2)
                                  ? startLabel.slice(0, -3)
                                  : startLabel;
                              return (
                                <StaggeredItem key={key + i} index={i}>
                                  <button
                                    onClick={() => toggleSlotInCart(slot)}
                                    className="group relative rounded-xl text-left transition-all duration-200 w-full h-full flex flex-col hover:-translate-y-0.5"
                                    style={
                                      inCart
                                        ? {
                                            backgroundColor: `${accentColor}08`,
                                            border: `2px solid ${accentColor}`,
                                            boxShadow: `0 0 0 3px ${accentColor}10`,
                                            padding: "14px 16px",
                                          }
                                        : {
                                            backgroundColor: "var(--bf-bg-card)",
                                            border: "2px solid var(--bf-border)",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                            padding: "14px 16px",
                                          }
                                    }
                                  >
                                    {/* Hover affordance — accent border fades in on hover when
                                      not selected, matching the service/coach cards. */}
                                    {!inCart && (
                                      <span
                                        className="pointer-events-none absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                        style={{ border: `2px solid ${accentColor}66` }}
                                      />
                                    )}
                                    {/* Header: time label + selection indicator share a flex row so the
                                      check never overlaps the time text. The indicator slot is always
                                      rendered (invisible when unselected) so cards don't reflow on select. */}
                                    <div className="flex items-start justify-between gap-2">
                                      {/* Time range on one line — the meridiem is shared unless the
                                        slot straddles noon (see startLabelShort above). */}
                                      <div className="min-w-0">
                                        <p
                                          className="font-semibold text-md whitespace-nowrap tracking-[-0.01em] leading-normal"
                                          style={{ color: inCart ? accentColor : "var(--bf-text)" }}
                                        >
                                          {startLabelShort} – {endLabel}
                                        </p>
                                      </div>
                                      <div
                                        aria-hidden={!inCart}
                                        className="h-5 w-5 shrink-0 rounded-full flex items-center justify-center text-white"
                                        style={
                                          inCart ? { backgroundColor: accentColor } : undefined
                                        }
                                      >
                                        {inCart && (
                                          <CheckIcon className="h-3 w-3" strokeWidth={3} />
                                        )}
                                      </div>
                                    </div>
                                    {/* Price — semi-private slots note "per participant" so the
                                      figure isn't mistaken for the whole-session total. */}
                                    <p
                                      className="text-sm font-medium mt-auto pt-2"
                                      style={{ color: "var(--bf-text-secondary)" }}
                                    >
                                      {formatPrice(slot.priceInCents)}
                                      {isSemiPrivate && slot.priceInCents > 0 && (
                                        <span
                                          className="font-normal"
                                          style={{ color: "var(--bf-text-muted)" }}
                                        >
                                          {" "}
                                          per participant
                                        </span>
                                      )}
                                    </p>
                                    {/* Instructor — own row so long names aren't truncated to "G…"
                                      when sharing horizontal space with the price. */}
                                    {!selectedCoachId && (
                                      <div className="flex items-center gap-1 mt-1.5 min-w-0">
                                        <UserIcon
                                          className="h-2.5 w-2.5 shrink-0"
                                          style={{ color: "var(--bf-text-muted)" }}
                                        />
                                        <span
                                          className="text-xs font-semibold truncate"
                                          style={{ color: "var(--bf-text)" }}
                                          title={slot.instructorName}
                                        >
                                          {slot.instructorName}
                                        </span>
                                      </div>
                                    )}
                                    {/* Location from offering */}
                                    {slot.locations[0]?.name && (
                                      <div className="flex items-center gap-1 mt-1.5 min-w-0">
                                        <MapPinIcon
                                          className="h-2.5 w-2.5 shrink-0"
                                          style={{ color: "var(--bf-text-muted)" }}
                                        />
                                        <span
                                          className="text-xs font-medium truncate"
                                          style={{ color: "var(--bf-text-muted)" }}
                                          title={slot.locations[0].name}
                                        >
                                          {slot.locations[0].name}
                                        </span>
                                      </div>
                                    )}
                                    {/* Semi-private badges */}
                                    {slot.existingSessionId && slot.spotsRemaining > 0 && (
                                      <span
                                        className="text-xs font-semibold px-1.5 py-[2px] rounded-full mt-1.5 inline-flex items-center gap-0.5 whitespace-nowrap"
                                        style={{
                                          backgroundColor: `${accentColor}08`,
                                          color: accentColor,
                                        }}
                                      >
                                        <UsersIcon className="h-2.5 w-2.5" />
                                        {slot.spotsRemaining} spot
                                        {slot.spotsRemaining !== 1 ? "s" : ""} left
                                      </span>
                                    )}
                                    {slot.existingSessionId && slot.spotsRemaining === 0 && (
                                      <span
                                        className="text-xs font-semibold px-1.5 py-[2px] rounded-full mt-1.5 inline-flex items-center gap-0.5 whitespace-nowrap"
                                        style={{
                                          backgroundColor: "rgba(107, 114, 128, 0.1)",
                                          color: "var(--bf-text-muted)",
                                        }}
                                      >
                                        <UsersIcon className="h-2.5 w-2.5" />
                                        Full
                                      </span>
                                    )}
                                    {!slot.existingSessionId && isSemiPrivate && (
                                      <span
                                        className="text-xs font-semibold px-1.5 py-[2px] rounded-full mt-1.5 inline-flex items-center gap-0.5 whitespace-nowrap"
                                        style={{
                                          color: "var(--bf-text-secondary)",
                                          backgroundColor: "var(--bf-bg-elevated)",
                                        }}
                                      >
                                        <UsersIcon className="h-2.5 w-2.5" />
                                        Up to {maxParticipants}
                                      </span>
                                    )}
                                  </button>
                                </StaggeredItem>
                              );
                            })}
                          </div>

                          {/* Quick-fill */}
                          {showQuickFill &&
                            recurringAllowed &&
                            cart.length === 1 &&
                            (selectedService?.maxParticipants ?? 1) <= 1 && (
                              <div
                                className="rounded-xl border border-dashed p-4 space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
                                style={{
                                  borderColor: "var(--bf-text-disabled)",
                                  backgroundColor: "var(--bf-bg-card)",
                                }}
                              >
                                <div className="flex items-center gap-2.5">
                                  <div
                                    className="h-8 w-8 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                                  >
                                    <ZapIcon
                                      className="h-4 w-4"
                                      style={{ color: "var(--bf-text-secondary)" }}
                                      strokeWidth={1.8}
                                    />
                                  </div>
                                  <div>
                                    <p
                                      className="text-sm font-semibold tracking-[-0.01em]"
                                      style={{ color: "var(--bf-text)" }}
                                    >
                                      Book recurring?
                                    </p>
                                    <p
                                      className="text-sm"
                                      style={{ color: "var(--bf-text-secondary)" }}
                                    >
                                      Same time every week
                                      {cart[0]?.instructorName && !selectedCoachId
                                        ? ` with ${cart[0].instructorName}`
                                        : ""}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {quickFillLoading ? (
                                    <div className="flex items-center gap-2 px-3.5 py-2">
                                      <LoaderCircleIcon
                                        className="h-4 w-4 animate-spin"
                                        style={{ color: "var(--bf-text-secondary)" }}
                                      />
                                      <span
                                        className="text-sm"
                                        style={{ color: "var(--bf-text-secondary)" }}
                                      >
                                        Checking availability...
                                      </span>
                                    </div>
                                  ) : (
                                    <>
                                      {[4, 8, 12].map((w) => (
                                        <button
                                          key={w}
                                          onClick={() => quickFillWeeks(w)}
                                          className="px-3.5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:shadow-sm hover:bg-black/[0.03] cursor-pointer"
                                          style={{
                                            color: "var(--bf-text)",
                                            border: "1px solid var(--bf-border)",
                                            backgroundColor: "var(--bf-bg-card)",
                                          }}
                                        >
                                          {w} weeks
                                        </button>
                                      ))}
                                      <button
                                        onClick={() => setShowQuickFill(false)}
                                        className="px-3.5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                                        style={{ color: "var(--bf-text-muted)" }}
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.color = "var(--bf-text-secondary)")
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.color = "var(--bf-text-muted)")
                                        }
                                      >
                                        Skip
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* End left column */}

            {/* ─── Right column: desktop sidebar ─── */}
            <div className="hidden lg:block">
              <div className="sticky top-[120px]">
                <div
                  className="rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: "var(--bf-bg-card)",
                    border: "1px solid var(--bf-border)",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  {cart.length === 0 ? (
                    <div className="px-5 py-10 text-center">
                      <div
                        className="h-12 w-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
                        style={
                          instructorExplicitlyChosen
                            ? { backgroundColor: accentColor }
                            : { backgroundColor: "var(--bf-bg-elevated)" }
                        }
                      >
                        {instructorExplicitlyChosen ? (
                          <CheckIcon className="h-5 w-5 text-white" strokeWidth={2.5} />
                        ) : (
                          <CalendarIcon
                            className="h-5 w-5"
                            strokeWidth={1.5}
                            style={{ color: "var(--bf-text-muted)" }}
                          />
                        )}
                      </div>
                      {/* Active next-step guidance once a specific instructor is
                          chosen; otherwise the passive summary placeholder. */}
                      {instructorExplicitlyChosen ? (
                        <>
                          <p
                            className="font-semibold text-md tracking-[-0.01em]"
                            style={{ color: "var(--bf-text)" }}
                          >
                            {selectedCoach?.name || "Instructor"} selected
                          </p>
                          <p
                            className="text-tiny mt-1.5 leading-relaxed max-w-[220px] mx-auto"
                            style={{ color: "var(--bf-text-muted)" }}
                          >
                            {nextStep.headline}
                          </p>
                          <Button
                            onClick={() => scrollToBookingStep(nextStep.target)}
                            className="mt-4 w-full h-10 text-white font-semibold text-sm gap-1.5 rounded-lg transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                            style={{ backgroundColor: accentColor }}
                          >
                            {nextStep.ctaLabel}
                            <ArrowRightIcon className="h-4 w-4" strokeWidth={2} />
                          </Button>
                        </>
                      ) : (
                        <>
                          <p
                            className="font-semibold text-md tracking-[-0.01em]"
                            style={{ color: "var(--bf-text)" }}
                          >
                            Booking Summary
                          </p>
                          <p
                            className="text-tiny mt-1.5 leading-relaxed max-w-[220px] mx-auto"
                            style={{ color: "var(--bf-text-muted)" }}
                          >
                            {!selectedServiceId
                              ? "Choose a service and pick a date to see available times"
                              : !selectedDate
                                ? "Pick a date on the calendar to see available times"
                                : "Select a time slot to add it to your booking"}
                          </p>
                        </>
                      )}
                      {/* Progress breadcrumbs */}
                      <div className="flex items-center justify-center gap-1.5 mt-4">
                        {(isDateFirstMode
                          ? [
                              { done: !!selectedServiceId, label: "Service" },
                              { done: !!selectedDate, label: "Date" },
                              { done: cart.length > 0, label: "Time" },
                            ]
                          : [
                              { done: !!selectedCoachId || singleCoach, label: "Instructor" },
                              { done: !!selectedServiceId, label: "Service" },
                              { done: !!selectedDate, label: "Date" },
                              { done: cart.length > 0, label: "Time" },
                            ]
                        ).map((step) => (
                          <span
                            key={step.label}
                            className="text-2xs font-medium px-2 py-0.5 rounded-full"
                            style={
                              step.done
                                ? { backgroundColor: `${accentColor}12`, color: accentColor }
                                : {
                                    backgroundColor: "var(--bf-bg-elevated)",
                                    color: "var(--bf-text-disabled)",
                                  }
                            }
                          >
                            {step.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div
                        className="px-5 py-4 flex items-center justify-between gap-2"
                        style={{ borderBottom: "1px solid var(--bf-border)" }}
                      >
                        <h3
                          className="font-semibold text-md tracking-[-0.01em]"
                          style={{ color: "var(--bf-text)" }}
                        >
                          Your selections
                        </h3>
                        <span
                          className="text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                          style={{
                            backgroundColor: "var(--bf-bg-elevated)",
                            color: "var(--bf-text-muted)",
                          }}
                        >
                          Not booked yet
                        </span>
                      </div>

                      {/* Coach + service context */}
                      {selectedService && (
                        <div
                          className="px-5 py-3 flex items-center gap-3"
                          style={{ borderBottom: "1px solid var(--bf-border)" }}
                        >
                          <CoachAvatar
                            name={
                              (scopedCoach || selectedCoach)?.name ??
                              cart[0]?.instructorName ??
                              null
                            }
                            image={
                              (scopedCoach || selectedCoach)?.image ??
                              cart[0]?.instructorImage ??
                              null
                            }
                            size={32}
                            accentColor={accentColor}
                          />
                          <div className="min-w-0 flex-1">
                            <p
                              className="text-sm font-semibold truncate"
                              style={{ color: "var(--bf-text)" }}
                            >
                              {selectedService.name}
                            </p>
                            <p className="text-xs" style={{ color: "var(--bf-text-secondary)" }}>
                              with{" "}
                              {(scopedCoach || selectedCoach)?.name ??
                                cart[0]?.instructorName ??
                                "Instructor"}{" "}
                              · {selectedService.durationMinutes} min
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Location — clear name + full address + Maps link */}
                      {cart[0]?.locations?.[0] && (
                        <div
                          className="px-5 py-3"
                          style={{ borderBottom: "1px solid var(--bf-border)" }}
                        >
                          <SummaryLocation
                            location={cart[0].locations[0]}
                            accentColor={accentColor}
                          />
                        </div>
                      )}

                      {/* Cart items */}
                      <div className="px-5 py-3">
                        <p
                          className="text-micro font-medium uppercase tracking-wider mb-2"
                          style={{ color: "var(--bf-text-muted)" }}
                        >
                          {cart.length === 1 ? "Selected session" : `${cart.length} sessions`}
                        </p>
                        <div className="space-y-1.5 max-h-52 overflow-y-auto">
                          {cart.map((s, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between py-2 px-2.5 rounded-lg group transition-colors"
                              style={{ backgroundColor: "var(--bf-bg)" }}
                            >
                              <div className="min-w-0">
                                <p
                                  className="text-sm font-medium"
                                  style={{ color: "var(--bf-text)" }}
                                >
                                  {formatDate(s.date + "T12:00:00")}
                                </p>
                                <p
                                  className="text-sm"
                                  style={{ color: "var(--bf-text-secondary)" }}
                                >
                                  {formatLocalTime(
                                    s.startTime,
                                    s.locations[0]?.timezone ?? timezone,
                                  )}{" "}
                                  –{" "}
                                  {formatLocalTime(s.endTime, s.locations[0]?.timezone ?? timezone)}
                                </p>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span
                                  className="text-sm font-semibold"
                                  style={{ color: accentColor }}
                                >
                                  {formatPrice(s.priceInCents)}
                                </span>
                                <button
                                  onClick={() => removeFromCart(i)}
                                  className="hover:text-red-500 p-0.5 rounded transition-colors opacity-0 group-hover:opacity-100"
                                  style={{ color: "var(--bf-text-disabled)" }}
                                  aria-label="Remove"
                                >
                                  <XIcon className="h-3 w-3" strokeWidth={2.5} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Total + CTA */}
                      <div
                        className="px-5 py-4"
                        style={{
                          borderTop: "1px solid var(--bf-border)",
                          backgroundColor: "var(--bf-bg-elevated)",
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span
                            className="text-tiny font-medium"
                            style={{ color: "var(--bf-text-secondary)" }}
                          >
                            Total
                          </span>
                          <span
                            className="text-lg font-bold tracking-[-0.02em]"
                            style={{ color: accentColor }}
                          >
                            {formatPrice(cartTotal)}
                          </span>
                        </div>
                        <Button
                          onClick={continueFromCart}
                          className="w-full h-11 text-white font-semibold text-md gap-2 rounded-lg transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                          style={{ backgroundColor: accentColor }}
                        >
                          Continue
                          <ArrowRightIcon className="h-4 w-4" strokeWidth={2} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Floating Cart — mobile only, sticky at bottom */}
            {cart.length > 0 && (
              <div
                className="lg:hidden sticky bottom-0 left-0 right-0 z-40 mt-3 pb-4 pt-2 animate-in slide-in-from-bottom-3 duration-300"
                style={{
                  background: isDark
                    ? "linear-gradient(to top, rgba(12,12,13,1) 60%, rgba(12,12,13,0))"
                    : "linear-gradient(to top, rgba(249,250,251,1) 60%, rgba(249,250,251,0))",
                }}
              >
                <div className="max-w-5xl mx-auto">
                  <div
                    className="rounded-2xl p-3.5"
                    style={{
                      backgroundColor: isDark ? "rgba(22,22,24,0.97)" : "rgba(255,255,255,0.97)",
                      backdropFilter: "blur(20px) saturate(180%)",
                      WebkitBackdropFilter: "blur(20px) saturate(180%)",
                      boxShadow: "0 -2px 12px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.08)",
                      border: "1px solid var(--bf-border)",
                    }}
                  >
                    <button
                      onClick={() => setCartExpanded(!cartExpanded)}
                      className="w-full flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2.5">
                        <span
                          className="flex h-7 w-7 items-center justify-center rounded-full text-white text-xs font-semibold"
                          style={{ backgroundColor: accentColor }}
                        >
                          {cart.length}
                        </span>
                        <div className="text-left">
                          <span
                            className="text-md font-semibold tracking-[-0.01em]"
                            style={{ color: "var(--bf-text)" }}
                          >
                            {cart.length} selected · not booked yet
                          </span>
                          <p className="text-xs" style={{ color: "var(--bf-text-secondary)" }}>
                            {cartExpanded ? "Collapse" : "View details"}
                          </p>
                        </div>
                      </div>
                      <span
                        className="text-lg font-semibold tracking-[-0.02em]"
                        style={{ color: accentColor }}
                      >
                        {formatPrice(cartTotal)}
                      </span>
                    </button>

                    {cartExpanded && (
                      <div
                        className="mt-3 max-h-48 overflow-y-auto rounded-lg divide-y"
                        style={{
                          backgroundColor: "var(--bf-bg-elevated)",
                          borderColor: "var(--bf-border)",
                        }}
                      >
                        {cart.map((s, i) => (
                          <div key={i} className="flex items-center justify-between py-2.5 px-3">
                            <div className="min-w-0">
                              <p
                                className="font-medium text-md"
                                style={{ color: "var(--bf-text)" }}
                              >
                                {formatDate(s.date + "T12:00:00")}
                              </p>
                              <p
                                className="text-xs mt-0.5"
                                style={{ color: "var(--bf-text-secondary)" }}
                              >
                                {formatLocalTime(s.startTime, s.locations[0]?.timezone ?? timezone)}{" "}
                                – {formatLocalTime(s.endTime, s.locations[0]?.timezone ?? timezone)}
                                {s.instructorName && ` · ${s.instructorName}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-2">
                              <span
                                className="font-semibold text-md"
                                style={{ color: accentColor }}
                              >
                                {formatPrice(s.priceInCents)}
                              </span>
                              <button
                                onClick={() => removeFromCart(i)}
                                className="hover:text-red-500 p-1 rounded-md transition-colors"
                                style={{ color: "var(--bf-text-muted)" }}
                                aria-label="Remove"
                              >
                                <XIcon className="h-3.5 w-3.5" strokeWidth={2} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <Button
                      onClick={continueFromCart}
                      className="w-full mt-3 h-11 text-white font-semibold text-md gap-2 rounded-lg transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                      style={{ backgroundColor: accentColor }}
                    >
                      Continue
                      <ArrowRightIcon className="h-4 w-4" strokeWidth={2} />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════ WHO PHASE ═══════ */}
        {phase === "who" &&
          (() => {
            const slot = cart[0];
            const spotsFilled = slot?.spotsFilled ?? 0;
            const spotsRemaining = isSemiPrivate ? maxParticipants - spotsFilled : 1;
            return (
              <div
                className={`space-y-5 max-w-2xl mx-auto pb-28 ${phaseDirection === "forward" ? "phase-enter-forward" : "phase-enter-back"}`}
                key="who"
              >
                <button
                  onClick={() => {
                    navigatePhase("pick");
                    setSelectedParticipants([]);
                  }}
                  className="text-md transition-colors flex items-center gap-1 font-medium group"
                  style={{ color: "var(--bf-text-secondary)" }}
                >
                  <ChevronLeftIcon
                    className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform"
                    strokeWidth={2}
                  />
                  Back
                </button>

                <div
                  className="rounded-2xl p-4 flex items-center gap-3"
                  style={{
                    backgroundColor: "var(--bf-bg-card)",
                    border: "1px solid var(--bf-border)",
                  }}
                >
                  <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                  >
                    <CalendarIcon
                      className="h-[17px] w-[17px]"
                      strokeWidth={1.8}
                      style={{ color: "var(--bf-text-secondary)" }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-semibold text-md tracking-[-0.01em]"
                      style={{ color: "var(--bf-text)" }}
                    >
                      {selectedService?.name || "Session"}
                    </p>
                    <p className="text-md" style={{ color: "var(--bf-text-secondary)" }}>
                      {cart.length} session{cart.length !== 1 ? "s" : ""} ·{" "}
                      {formatPrice(baseCartTotal)}
                    </p>
                  </div>
                </div>

                <div>
                  <h2
                    className="text-xl font-semibold tracking-[-0.02em]"
                    style={{ color: "var(--bf-text)" }}
                  >
                    Who is this for?
                  </h2>
                  {isSemiPrivate ? (
                    <>
                      <p
                        className="text-md mt-1 leading-relaxed"
                        style={{ color: "var(--bf-text-secondary)" }}
                      >
                        Add up to {spotsRemaining} participant{spotsRemaining !== 1 ? "s" : ""}.
                        {selectedParticipants.length > 0 && (
                          <span className="font-semibold" style={{ color: accentColor }}>
                            {" "}
                            {selectedParticipants.length} selected
                          </span>
                        )}
                      </p>
                      <p
                        className="text-sm mt-1 leading-relaxed"
                        style={{ color: "var(--bf-text-muted)" }}
                      >
                        Pricing is per participant — your total updates as you add participants.
                      </p>
                    </>
                  ) : participants.length > 0 &&
                    selectedParticipants.length === 0 &&
                    !showAddForm ? (
                    <p className="text-md mt-1" style={{ color: "var(--bf-text-secondary)" }}>
                      Select a participant to continue.
                    </p>
                  ) : null}
                </div>

                {/* Add Participant Form */}
                {(participants.length === 0 || showAddForm) && (
                  <div
                    className="rounded-2xl overflow-hidden animate-in fade-in duration-300"
                    style={{
                      backgroundColor: "var(--bf-bg-card)",
                      border: "1px solid var(--bf-border)",
                    }}
                  >
                    <div
                      className="px-5 py-3.5"
                      style={{ borderBottom: "1px solid var(--bf-border)" }}
                    >
                      <h3
                        className="font-semibold text-md tracking-[-0.01em]"
                        style={{ color: "var(--bf-text)" }}
                      >
                        {participants.length === 0
                          ? "Add a Participant"
                          : "Add Another Participant"}
                      </h3>
                      <p className="text-md mt-0.5" style={{ color: "var(--bf-text-secondary)" }}>
                        Who will be attending this session?
                      </p>
                    </div>
                    <div className="p-5 space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label
                            className="text-xs font-medium uppercase tracking-wider mb-1.5 block"
                            style={{ color: "var(--bf-text-secondary)" }}
                          >
                            First Name
                          </label>
                          <input
                            type="text"
                            value={newFirstName}
                            onChange={(e) => setNewFirstName(e.target.value)}
                            placeholder="First name"
                            className="w-full rounded-lg px-3.5 py-2.5 text-md focus:outline-none focus:ring-2 transition-all"
                            style={
                              {
                                backgroundColor: "var(--bf-bg)",
                                color: "var(--bf-text)",
                                border: "1px solid var(--bf-border)",
                                "--webkit-autofill-selection-background": accentColor,
                              } as React.CSSProperties
                            }
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = accentColor;
                              e.currentTarget.style.boxShadow = `0 0 0 3px ${accentColor}10`;
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = getComputedStyle(
                                document.documentElement,
                              )
                                .getPropertyValue("--bf-border")
                                .trim();
                              e.currentTarget.style.boxShadow = "none";
                            }}
                            autoFocus
                          />
                        </div>
                        <div>
                          <label
                            className="text-xs font-medium uppercase tracking-wider mb-1.5 block"
                            style={{ color: "var(--bf-text-secondary)" }}
                          >
                            Last Name
                          </label>
                          <input
                            type="text"
                            value={newLastName}
                            onChange={(e) => setNewLastName(e.target.value)}
                            placeholder="Last name"
                            className="w-full rounded-lg px-3.5 py-2.5 text-md focus:outline-none focus:ring-2 transition-all"
                            style={{
                              backgroundColor: "var(--bf-bg)",
                              color: "var(--bf-text)",
                              border: "1px solid var(--bf-border)",
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = accentColor;
                              e.currentTarget.style.boxShadow = `0 0 0 3px ${accentColor}10`;
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = getComputedStyle(
                                document.documentElement,
                              )
                                .getPropertyValue("--bf-border")
                                .trim();
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          className="text-xs font-medium uppercase tracking-wider mb-1.5 block"
                          style={{ color: "var(--bf-text-secondary)" }}
                        >
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          max={latestDateOfBirth()}
                          value={newDob}
                          onChange={(e) => setNewDob(e.target.value)}
                          required
                          className="w-full rounded-lg px-3.5 py-2.5 text-md focus:outline-none focus:ring-2 transition-all"
                          style={{
                            backgroundColor: "var(--bf-bg)",
                            color: "var(--bf-text)",
                            border: "1px solid var(--bf-border)",
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = accentColor;
                            e.currentTarget.style.boxShadow = `0 0 0 3px ${accentColor}10`;
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = getComputedStyle(
                              document.documentElement,
                            )
                              .getPropertyValue("--bf-border")
                              .trim();
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        />
                      </div>
                      {error && (
                        <p className="text-md text-red-500 font-medium flex items-center gap-1.5">
                          <XIcon className="h-3.5 w-3.5" />
                          {error}
                        </p>
                      )}
                      <div className="flex gap-3">
                        {participants.length > 0 && (
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowAddForm(false);
                              setNewFirstName("");
                              setNewLastName("");
                              setNewDob("");
                              setError("");
                            }}
                            className="flex-1 h-11 rounded-lg font-medium text-md"
                            style={{
                              border: "1px solid var(--bf-border)",
                              color: "var(--bf-text-secondary)",
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                        <Button
                          disabled={
                            !newFirstName.trim() ||
                            !newLastName.trim() ||
                            !newDob ||
                            addingParticipant
                          }
                          onClick={async () => {
                            setAddingParticipant(true);
                            setError("");
                            try {
                              const res = await fetch("/api/participants", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  firstName: newFirstName.trim(),
                                  lastName: newLastName.trim(),
                                  dateOfBirth: newDob,
                                }),
                              });
                              if (!res.ok) {
                                const data = await res.json().catch(() => ({}));
                                setError(data.error || "Failed to add participant.");
                                setAddingParticipant(false);
                                return;
                              }
                              const data = await res.json();
                              const newP = data.participant;
                              if (newP) {
                                setParticipants((prev) => [
                                  ...prev,
                                  {
                                    id: newP.id,
                                    firstName: newP.firstName,
                                    lastName: newP.lastName,
                                    dateOfBirth: newP.dateOfBirth,
                                  },
                                ]);
                                setNewFirstName("");
                                setNewLastName("");
                                setNewDob("");
                                setShowAddForm(false);
                                if (participants.length === 0 && !isSemiPrivate) {
                                  void pickParticipant({
                                    id: newP.id,
                                    firstName: newP.firstName,
                                    lastName: newP.lastName,
                                    dateOfBirth: newP.dateOfBirth,
                                  });
                                }
                              }
                            } catch {
                              setError("Something went wrong. Please try again.");
                            }
                            setAddingParticipant(false);
                          }}
                          className="flex-1 h-11 text-white font-semibold text-md rounded-lg gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
                          style={{ backgroundColor: accentColor }}
                        >
                          {addingParticipant ? (
                            <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <UserPlusIcon className="h-4 w-4" />
                              Add Participant
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {participants.length > 0 && !showAddForm && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="w-full rounded-2xl border border-dashed py-3.5 text-center transition-all hover:shadow-sm group"
                    style={{
                      borderColor: "var(--bf-text-disabled)",
                      backgroundColor: "var(--bf-bg-card)",
                    }}
                  >
                    <span
                      className="text-md font-medium flex items-center justify-center gap-2"
                      style={{ color: accentColor }}
                    >
                      <UserPlusIcon className="h-4 w-4" />
                      Add Another Participant
                    </span>
                  </button>
                )}

                {participants.length > 0 && !showAddForm ? (
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    {participants.map((p, i) => {
                      const st = waiverStatusFor(p.id, waiverStatuses);
                      const isSelected = selectedParticipants.some((sp) => sp.id === p.id);
                      return (
                        <StaggeredItem key={p.id} index={i}>
                          <SelectableCard
                            isSelected={isSelected}
                            accentColor={accentColor}
                            onClick={() => pickParticipant(p)}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <p
                                  className="font-semibold text-md tracking-[-0.01em]"
                                  style={{ color: "var(--bf-text)" }}
                                >
                                  {p.firstName} {p.lastName}
                                </p>
                                {(st === "unsigned" || st === "expired") && (
                                  <p
                                    className="text-md mt-0.5"
                                    style={{ color: "var(--bf-text-secondary)" }}
                                  >
                                    Waiver needed
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <WaiverBadge status={st} />
                                {isSelected && (
                                  <span
                                    className="flex h-5 w-5 items-center justify-center rounded-full text-white"
                                    style={{ backgroundColor: accentColor }}
                                  >
                                    <CheckIcon className="h-3 w-3" strokeWidth={3} />
                                  </span>
                                )}
                              </div>
                            </div>
                          </SelectableCard>
                        </StaggeredItem>
                      );
                    })}
                  </div>
                ) : null}

                {/* Inline waiver notice */}
                {selectedParticipants.some((p) => {
                  const st = waiverStatusFor(p.id, waiverStatuses);
                  return st === "unsigned" || st === "expired";
                }) &&
                  (() => {
                    const p = selectedParticipants.find((pp) => {
                      const st = waiverStatusFor(pp.id, waiverStatuses);
                      return st === "unsigned" || st === "expired";
                    });
                    const st = p ? waiverStatusFor(p.id, waiverStatuses) : null;
                    const isExpired = st === "expired";
                    return (
                      <div
                        className="rounded-2xl p-4 flex items-start gap-3"
                        style={{
                          backgroundColor: `${accentColor}08`,
                          border: `1px solid ${accentColor}20`,
                        }}
                      >
                        <ShieldCheckIcon
                          className="h-5 w-5 shrink-0 mt-0.5"
                          style={{ color: accentColor }}
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-md" style={{ color: "var(--bf-text)" }}>
                            {isExpired ? "Waiver expired" : "Waiver required"}
                          </p>
                          <p
                            className="text-tiny mt-0.5 leading-relaxed"
                            style={{ color: "var(--bf-text-secondary)" }}
                          >
                            {isExpired
                              ? `${p?.firstName}'s waiver has expired. Please sign a new one before booking.`
                              : "A signed waiver is needed before booking."}{" "}
                            <button
                              type="button"
                              className="underline font-medium"
                              style={{ color: accentColor }}
                              onClick={() => {
                                if (p)
                                  setWaiverModal({
                                    participantId: p.id,
                                    participantName: `${p.firstName} ${p.lastName}`,
                                    participantDateOfBirth: p.dateOfBirth || null,
                                  });
                              }}
                            >
                              {isExpired ? "Sign new waiver" : "Review & sign now"}
                            </button>
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                {isSemiPrivate && (
                  <div className="space-y-3">
                    {selectedParticipants.length > 0 && (
                      <div
                        className="rounded-2xl p-4 space-y-1"
                        style={{
                          backgroundColor: "var(--bf-bg-card)",
                          border: "1px solid var(--bf-border)",
                        }}
                      >
                        <p
                          className="text-xs font-medium uppercase tracking-wider mb-2"
                          style={{ color: "var(--bf-text-muted)" }}
                        >
                          Selected
                        </p>
                        {selectedParticipants.map((p) => (
                          <div
                            key={p.id}
                            className="flex items-center justify-between py-1.5 px-2 rounded-md transition-colors"
                            style={{ backgroundColor: "var(--bf-bg)" }}
                          >
                            <span
                              className="text-md font-medium"
                              style={{ color: "var(--bf-text)" }}
                            >
                              {p.firstName} {p.lastName}
                            </span>
                            <button
                              onClick={() =>
                                setSelectedParticipants((prev) =>
                                  prev.filter((sp) => sp.id !== p.id),
                                )
                              }
                              className="text-xs font-medium px-2 py-1 rounded transition-colors hover:text-red-500"
                              style={{ color: "var(--bf-text-secondary)" }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {spotsRemaining > selectedParticipants.length && (
                      <div
                        className="rounded-2xl border border-dashed p-4 flex items-center gap-3"
                        style={{
                          backgroundColor: "var(--bf-bg-card)",
                          borderColor: "var(--bf-text-disabled)",
                        }}
                      >
                        <div
                          className="flex h-9 w-9 items-center justify-center rounded-lg shrink-0"
                          style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                        >
                          <UserPlusIcon
                            className="h-[17px] w-[17px]"
                            strokeWidth={1.8}
                            style={{ color: "var(--bf-text-secondary)" }}
                          />
                        </div>
                        <div>
                          <p className="font-semibold text-md" style={{ color: "var(--bf-text)" }}>
                            Invite another family?
                          </p>
                          <p
                            className="text-md mt-0.5"
                            style={{ color: "var(--bf-text-secondary)" }}
                          >
                            {spotsRemaining - selectedParticipants.length} spot
                            {spotsRemaining - selectedParticipants.length !== 1 ? "s" : ""} left.
                            Share an invite link after booking.
                          </p>
                        </div>
                      </div>
                    )}

                    {selectedParticipants.length > 0 && (
                      <>
                        <div
                          className="flex items-center justify-between px-0.5"
                          style={{ color: "var(--bf-text-secondary)" }}
                        >
                          <span className="text-md">
                            {formatPrice(cart[0]?.priceInCents ?? 0)} per participant ×{" "}
                            {selectedParticipants.length}
                          </span>
                          <span
                            className="text-lg font-semibold tracking-[-0.02em]"
                            style={{ color: accentColor }}
                          >
                            {formatPrice(
                              bookingTotalInCents(
                                cart[0]?.priceInCents ?? 0,
                                maxParticipants,
                                selectedParticipants.length,
                              ),
                            )}
                          </span>
                        </div>
                        <Button
                          onClick={() => continueFromWho()}
                          className="w-full h-11 text-white font-semibold text-md rounded-lg gap-2 transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                          style={{ backgroundColor: accentColor }}
                        >
                          Continue <ArrowRightIcon className="h-4 w-4" strokeWidth={2} />
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {/* Desktop Continue for standard (non-semi-private) lessons */}
                {!isSemiPrivate && selectedParticipants.length > 0 && (
                  <div className="hidden lg:flex flex-col gap-3">
                    <div
                      className="flex items-center justify-between px-0.5"
                      style={{ color: "var(--bf-text-secondary)" }}
                    >
                      <span className="text-md">
                        {formatPrice(cart[0]?.priceInCents ?? 0)} per lesson
                      </span>
                      <span
                        className="text-lg font-semibold tracking-[-0.02em]"
                        style={{ color: accentColor }}
                      >
                        {formatPrice((cart[0]?.priceInCents ?? 0) * selectedParticipants.length)}
                      </span>
                    </div>
                    <Button
                      onClick={() => continueFromWho()}
                      className="w-full h-11 text-white font-semibold text-md rounded-lg gap-2 transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                      style={{ backgroundColor: accentColor }}
                    >
                      {selectedParticipants.some((p) => {
                        const st = waiverStatusFor(p.id, waiverStatuses);
                        return st === "unsigned" || st === "expired";
                      })
                        ? "Sign Waiver & Continue"
                        : "Continue"}{" "}
                      <ArrowRightIcon className="h-4 w-4" strokeWidth={2} />
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}

        {/* ═══════ CONFIRM PHASE ═══════ */}
        {phase === "confirm" &&
          selectedService &&
          selectedParticipants.length > 0 &&
          cart.length > 0 && (
            <div
              className={`space-y-5 max-w-lg mx-auto pb-28 ${phaseDirection === "forward" ? "phase-enter-forward" : "phase-enter-back"}`}
              key="confirm"
            >
              <button
                onClick={() => navigatePhase("who")}
                className="text-sm flex items-center gap-1 font-medium group"
                style={{ color: "var(--bf-text-secondary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--bf-text)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--bf-text-secondary)")}
              >
                <ChevronLeftIcon
                  className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform"
                  strokeWidth={2}
                />
                Back
              </button>
              <h2
                className="text-xl font-semibold tracking-[-0.02em]"
                style={{ color: "var(--bf-text)" }}
              >
                Review & Confirm
              </h2>

              {/* "Not booked yet" notice — the receipt card below reads like a
                finished receipt; parents were leaving this step thinking
                they were booked. Make the remaining action unmistakable. */}
              <div
                className="rounded-xl p-3.5"
                style={{
                  backgroundColor: `${accentColor}0F`,
                  border: `1px solid ${accentColor}40`,
                }}
              >
                <p className="text-sm leading-relaxed" style={{ color: "var(--bf-text)" }}>
                  <span className="font-semibold">
                    Almost done — your booking isn&apos;t confirmed yet.
                  </span>{" "}
                  Review the details below, then tap the button below to finish.
                </p>
              </div>

              {/* Receipt card */}
              <div
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: "var(--bf-bg-card)",
                  border: "1px solid var(--bf-border)",
                }}
              >
                <div className="h-[3px]" style={{ backgroundColor: accentColor }} />
                <div className="p-5 space-y-3.5">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-9 w-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                    >
                      <BookOpenIcon
                        className="h-[17px] w-[17px]"
                        style={{ color: "var(--bf-text-secondary)" }}
                        strokeWidth={1.8}
                      />
                    </div>
                    <div className="flex flex-1 items-center justify-between min-w-0">
                      <span className="text-sm" style={{ color: "var(--bf-text-secondary)" }}>
                        Service
                      </span>
                      <span className="text-sm font-semibold" style={{ color: "var(--bf-text)" }}>
                        {selectedService.name}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div
                      className="h-9 w-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                    >
                      {selectedParticipants.length > 1 ? (
                        <UsersIcon
                          className="h-[17px] w-[17px]"
                          style={{ color: "var(--bf-text-secondary)" }}
                          strokeWidth={1.8}
                        />
                      ) : (
                        <UserIcon
                          className="h-[17px] w-[17px]"
                          style={{ color: "var(--bf-text-secondary)" }}
                          strokeWidth={1.8}
                        />
                      )}
                    </div>
                    <div className="flex flex-1 items-center justify-between min-w-0">
                      <span className="text-sm" style={{ color: "var(--bf-text-secondary)" }}>
                        For
                      </span>
                      <span
                        className="text-sm font-semibold text-right"
                        style={{ color: "var(--bf-text)" }}
                      >
                        {selectedParticipants.map((p) => `${p.firstName} ${p.lastName}`).join(", ")}
                      </span>
                    </div>
                  </div>

                  <div className="my-0.5 h-px" style={{ backgroundColor: "var(--bf-border)" }} />

                  {cart.map((s, i) => (
                    <div key={i} className="flex items-start justify-between py-0.5 gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm" style={{ color: "var(--bf-text)" }}>
                          {formatDate(s.date + "T12:00:00")}
                        </p>
                        <p className="text-sm mt-0.5" style={{ color: "var(--bf-text-secondary)" }}>
                          {formatLocalTime(s.startTime, s.locations[0]?.timezone ?? timezone)} –{" "}
                          {formatLocalTime(s.endTime, s.locations[0]?.timezone ?? timezone)}
                        </p>
                      </div>
                      <span
                        className="font-semibold text-sm shrink-0"
                        style={{ color: accentColor }}
                      >
                        {formatPrice(s.priceInCents * selectedParticipants.length)}
                      </span>
                    </div>
                  ))}

                  {cart[0]?.locations?.[0] && (
                    <>
                      <div
                        className="my-0.5 h-px"
                        style={{ backgroundColor: "var(--bf-border)" }}
                      />
                      <SummaryLocation location={cart[0].locations[0]} accentColor={accentColor} />
                    </>
                  )}

                  <div className="my-0.5 h-px" style={{ backgroundColor: "var(--bf-border)" }} />

                  <div className="flex items-center justify-between">
                    <span
                      className="text-sm font-medium"
                      style={{ color: "var(--bf-text-secondary)" }}
                    >
                      Total
                    </span>
                    <span
                      className="text-xl font-semibold tracking-[-0.02em]"
                      style={{ color: accentColor }}
                    >
                      {formatPrice(cartTotal)}
                    </span>
                  </div>

                  {/* Per-participant breakdown so the semi-private total is unambiguous. */}
                  {isSemiPrivate && selectedParticipants.length > 0 && (
                    <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                      {formatPrice(cart[0]?.priceInCents ?? 0)} × {selectedParticipants.length}{" "}
                      participant{selectedParticipants.length !== 1 ? "s" : ""} ={" "}
                      {formatPrice(cartTotal)} total
                    </p>
                  )}

                  <p className="text-xs leading-relaxed" style={{ color: "var(--bf-text-muted)" }}>
                    {cartTotal === 0
                      ? "This session is free — no payment required."
                      : `Pay ${selectedCoach?.name || "the instructor"} at your session — no online payment required.`}
                  </p>
                </div>
              </div>

              {isSemiPrivate &&
                maxParticipants - selectedParticipants.length - (cart[0]?.spotsFilled ?? 0) > 0 && (
                  <div
                    className="flex items-center gap-3 rounded-xl p-4"
                    style={{
                      backgroundColor: "var(--bf-bg-card)",
                      border: "1px solid var(--bf-border)",
                    }}
                  >
                    <UserPlusIcon
                      className="h-4 w-4 shrink-0"
                      style={{ color: "var(--bf-text-muted)" }}
                      strokeWidth={1.8}
                    />
                    <p className="text-sm" style={{ color: "var(--bf-text-secondary)" }}>
                      <span className="font-semibold" style={{ color: "var(--bf-text)" }}>
                        Open spots remaining.
                      </span>{" "}
                      After booking, you&apos;ll get a shareable link to invite others to join this
                      session.
                    </p>
                  </div>
                )}

              {policies.length > 0 && (
                <div
                  className="rounded-xl p-5 space-y-3"
                  style={{
                    backgroundColor: "var(--bf-bg-card)",
                    border: "1px solid var(--bf-border)",
                  }}
                >
                  <p
                    className="text-xs font-medium uppercase tracking-wider"
                    style={{ color: "var(--bf-text-muted)" }}
                  >
                    Booking Policies
                  </p>
                  <ul className="space-y-2">
                    {policies.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-start gap-2.5 text-sm leading-relaxed"
                        style={{ color: "var(--bf-text-secondary)" }}
                      >
                        <span
                          className="mt-[7px] h-1 w-1 rounded-full shrink-0"
                          style={{ backgroundColor: accentColor }}
                        />
                        {p.text}
                      </li>
                    ))}
                  </ul>
                  <label
                    className="flex items-start gap-3 cursor-pointer pt-3"
                    style={{ borderTop: "1px solid var(--bf-border)" }}
                  >
                    <input
                      type="checkbox"
                      checked={policiesAcknowledged}
                      onChange={(e) => setPoliciesAcknowledged(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded"
                      style={{ accentColor }}
                    />
                    <span className="text-sm font-medium" style={{ color: "var(--bf-text)" }}>
                      I agree to the booking policies
                    </span>
                  </label>
                </div>
              )}

              <Button
                className="w-full h-12 text-base font-semibold text-white gap-2 rounded-xl transition-all duration-200 hover:opacity-90 active:scale-[0.98] hidden lg:flex"
                style={{ backgroundColor: accentColor, boxShadow: `0 4px 12px ${accentColor}25` }}
                disabled={submitting || (policies.length > 0 && !policiesAcknowledged)}
                onClick={handleConfirm}
              >
                {submitting && <LoaderCircleIcon className="h-4 w-4 animate-spin" />}
                {submitting
                  ? cart.length > 1
                    ? `Booking ${cart.length} sessions...`
                    : "Booking..."
                  : cart.length > 1
                    ? `Book ${cart.length} Sessions`
                    : "Confirm Booking"}
              </Button>
            </div>
          )}

        {/* ═══════ DONE PHASE ═══════ */}
        {phase === "done" && (
          <div
            className={`space-y-5 max-w-lg mx-auto ${phaseDirection === "forward" ? "phase-enter-forward" : "phase-enter-back"}`}
            key="done"
          >
            <div className="text-center pt-8">
              <div className="relative mx-auto mb-5 h-16 w-16">
                <div
                  className="absolute inset-0 rounded-full opacity-10"
                  style={{ backgroundColor: accentColor }}
                />
                <div
                  className="relative flex h-16 w-16 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${accentColor}08` }}
                >
                  <CheckCircle2Icon
                    className="h-8 w-8"
                    style={{ color: accentColor }}
                    strokeWidth={1.5}
                  />
                </div>
              </div>
              <h2
                className="text-xl font-semibold tracking-[-0.02em]"
                style={{ color: "var(--bf-text)" }}
              >
                Confirmed
              </h2>
              <p
                className="text-sm mt-1.5 leading-relaxed"
                style={{ color: "var(--bf-text-secondary)" }}
              >
                {selectedParticipants.map((p) => p.firstName).join(" & ")}&apos;s session is booked.
              </p>
            </div>

            {spotsRemainingAfterBooking > 0 && bookingShareUrl && (
              <div
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: "var(--bf-bg-card)",
                  border: "1px solid var(--bf-border)",
                }}
              >
                <div className="h-[3px]" style={{ backgroundColor: accentColor }} />
                <div className="p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-lg shrink-0"
                      style={{ backgroundColor: "var(--bf-bg-elevated)" }}
                    >
                      <UserPlusIcon
                        className="h-[17px] w-[17px]"
                        style={{ color: "var(--bf-text-secondary)" }}
                        strokeWidth={1.8}
                      />
                    </div>
                    <div>
                      <p className="font-semibold text-sm" style={{ color: "var(--bf-text)" }}>
                        Invite Someone
                      </p>
                      <p className="text-sm" style={{ color: "var(--bf-text-secondary)" }}>
                        {spotsRemainingAfterBooking} spot
                        {spotsRemainingAfterBooking !== 1 ? "s" : ""} remaining
                      </p>
                    </div>
                  </div>
                  <div
                    className="rounded-lg p-3 space-y-2"
                    style={{ backgroundColor: "var(--bf-bg)" }}
                  >
                    <p
                      className="text-xs font-medium uppercase tracking-wider"
                      style={{ color: "var(--bf-text-muted)" }}
                    >
                      Share this link to invite others
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={bookingShareUrl}
                        className="flex-1 rounded-md px-3 py-2 text-xs font-mono select-all"
                        style={{
                          backgroundColor: "var(--bf-bg-card)",
                          color: "var(--bf-text)",
                          border: "1px solid var(--bf-border)",
                        }}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                      <Button
                        size="sm"
                        className="shrink-0 gap-1.5 text-white min-h-[36px] rounded-md font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
                        style={{ backgroundColor: accentColor }}
                        onClick={() => {
                          navigator.clipboard
                            ?.writeText(bookingShareUrl)
                            .then(() => {
                              setInviteCopied(true);
                              setTimeout(() => setInviteCopied(false), 3000);
                            })
                            .catch(() => {
                              window.prompt("Copy this invite link:", bookingShareUrl);
                            });
                        }}
                      >
                        {inviteCopied ? (
                          <>
                            <CheckIcon className="h-3.5 w-3.5" />
                            Copied
                          </>
                        ) : (
                          <>
                            <LinkIcon className="h-3.5 w-3.5" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  {typeof navigator !== "undefined" && navigator.share && (
                    <Button
                      variant="outline"
                      className="w-full gap-2 min-h-[40px] rounded-lg font-medium"
                      style={{ color: "var(--bf-text)", border: "1px solid var(--bf-border)" }}
                      onClick={() => {
                        navigator
                          .share({
                            title: `Join my ${selectedService?.name || "session"}`,
                            text: "I booked a session and there's a spot for you!",
                            url: bookingShareUrl,
                          })
                          .catch(() => {});
                      }}
                    >
                      <SendIcon className="h-4 w-4" strokeWidth={1.8} />
                      Share
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Button
                className="w-full h-11 text-white font-semibold text-sm rounded-lg transition-all hover:opacity-90 active:scale-[0.98]"
                style={{ backgroundColor: accentColor }}
                onClick={() => router.push(orgHref("/dashboard"))}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                className="w-full h-11 text-sm font-semibold rounded-lg gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
                style={{ color: accentColor, borderColor: accentColor }}
                onClick={() => router.push(orgHref("/book"))}
              >
                <CalendarIcon className="h-4 w-4" strokeWidth={1.8} />
                Book Another Session
              </Button>
              <Button
                variant="ghost"
                className="w-full text-sm font-medium"
                style={{ color: "var(--bf-text-secondary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--bf-text)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--bf-text-secondary)")}
                onClick={() => router.push(orgHref("/bookings"))}
              >
                View Booking History
              </Button>
            </div>
          </div>
        )}

        {/* Footer spacer */}
        <div className="mt-12 pb-6" />
      </div>

      {/* ═══════ UNIFIED MOBILE FOOTER ═══════ */}
      {phase !== "done" && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 lg:hidden"
          style={{
            backgroundColor: isDark ? "rgba(12,12,13,0.97)" : "rgba(255,255,255,0.97)",
            borderTop: "1px solid var(--bf-border)",
            boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <div className="max-w-5xl mx-auto px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            {/* Pick phase: full-width tappable footer.
                Earlier this was a split row with the cart-summary as a non-
                interactive <div> on the left and a "Next" button on the
                right. Grace flagged that tapping the summary did nothing —
                she expected the whole footer to advance the flow. The
                entire region is now one button so a tap anywhere advances. */}
            {phase === "pick" && cart.length > 0 && (
              <button
                type="button"
                onClick={continueFromCart}
                aria-label={`Continue with ${cart.length} ${cart.length === 1 ? "session" : "sessions"}`}
                className="w-full flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-left transition-all active:scale-[0.99]"
                style={{ backgroundColor: accentColor, color: "white" }}
              >
                <span className="text-md font-semibold">
                  {cart.length} {cart.length === 1 ? "session" : "sessions"} ·{" "}
                  {formatPrice(cartTotal)}
                </span>
                <span className="inline-flex items-center gap-1 text-md font-semibold">
                  Continue
                  <ArrowRightIcon className="h-4 w-4" />
                </span>
              </button>
            )}

            {/* Pick phase, no slot yet: once an instructor is chosen this
                footer would otherwise sit empty. Surface the next-step CTA so
                mobile users (who can't see the desktop summary) know to keep
                going. Two rows so the *status* (what's chosen) reads separately
                from the *action* button — a single combined button tested as
                ambiguous on iPhone. It lives inside this single unified footer
                — never a second fixed bar — so it can't stack over the cart CTA. */}
            {phase === "pick" && cart.length === 0 && instructorExplicitlyChosen && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 px-1 min-w-0">
                  <span
                    className="flex items-center justify-center h-4 w-4 rounded-full shrink-0"
                    style={{ backgroundColor: accentColor }}
                  >
                    <CheckIcon className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                  </span>
                  <span
                    className="text-sm font-medium truncate"
                    style={{ color: "var(--bf-text-secondary)" }}
                  >
                    {selectedCoach?.name || "Instructor"}
                    {selectedService ? ` · ${selectedService.name}` : " selected"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => scrollToBookingStep(nextStep.target)}
                  aria-label={nextStep.ctaLabel}
                  className="w-full flex items-center justify-center gap-1.5 rounded-xl px-4 py-3 text-md font-semibold transition-all active:scale-[0.99]"
                  style={{ backgroundColor: accentColor, color: "white" }}
                >
                  {nextStep.ctaLabel}
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Who phase: back + continue */}
            {phase === "who" && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    navigatePhase("pick");
                    setSelectedParticipants([]);
                  }}
                  className="h-11 px-3 rounded-xl flex items-center gap-1 text-md font-medium transition-colors"
                  style={{
                    color: "var(--bf-text-secondary)",
                    border: "1px solid var(--bf-border)",
                  }}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Back
                </button>
                <button
                  onClick={() => continueFromWho()}
                  disabled={selectedParticipants.length === 0}
                  className="flex-1 h-11 rounded-xl text-white font-semibold text-md transition-all active:scale-[0.97] disabled:opacity-40"
                  style={{ backgroundColor: accentColor }}
                >
                  {selectedParticipants.some((p) => {
                    const st = waiverStatusFor(p.id, waiverStatuses);
                    return st === "unsigned" || st === "expired";
                  })
                    ? "Sign Waiver & Continue"
                    : "Continue"}
                </button>
              </div>
            )}

            {/* Confirm phase: back + complete */}
            {phase === "confirm" && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigatePhase("who")}
                  className="h-11 px-3 rounded-xl flex items-center gap-1 text-md font-medium transition-colors"
                  style={{
                    color: "var(--bf-text-secondary)",
                    border: "1px solid var(--bf-border)",
                  }}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Back
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={submitting || (policies.length > 0 && !policiesAcknowledged)}
                  className="flex-1 h-11 rounded-xl text-white font-semibold text-md transition-all active:scale-[0.97] disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{ backgroundColor: accentColor }}
                >
                  {submitting ? <LoaderCircleIcon className="h-4 w-4 animate-spin" /> : null}
                  {submitting ? "Booking..." : "Confirm Booking"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Waiver modal */}
      {waiverModal && (
        <WaiverModal
          open={true}
          onOpenChange={(o) => {
            if (!o) setWaiverModal(null);
          }}
          participantId={waiverModal.participantId}
          participantName={waiverModal.participantName}
          participantDateOfBirth={waiverModal.participantDateOfBirth}
          onSigned={() => {
            setWaiverModal(null);
            void fetch("/api/rpc/participants")
              .then((r) => r.json())
              .then((d) => {
                const list = d.participants || [];
                setParticipants(list);
                const fresh = toWaiverStatuses(list);
                setWaiverStatuses(fresh);
                // Pass fresh data directly — closure waiverStatuses is stale until next render
                void continueFromWho(fresh);
              });
          }}
        />
      )}

      {/* Coach profile detail sheet — opened from each card's "View profile"
          footer. Reuses the same component the public storefront uses
          (PR #52); here we override the sticky CTA to select-in-place
          instead of navigating, and hide the share link so parents
          aren't pulled away from their in-progress booking. */}
      <CoachDetailSheet
        coach={
          aboutCoachId
            ? (() => {
                const c = coaches.find((x) => x.id === aboutCoachId);
                return c ? bookingCoachToSheetCoach(c) : null;
              })()
            : null
        }
        open={aboutCoachId !== null}
        onOpenChange={(open) => {
          if (!open) setAboutCoachId(null);
        }}
        orgSlug={org.slug}
        orgServices={allServices.map((s) => ({
          id: s.id,
          name: s.name,
          durationMinutes: s.durationMinutes,
          priceInCents: s.priceInCents,
        }))}
        accent={accentColor}
        hideShareLink
        closeLabel="Back to booking"
        primaryLabel={
          aboutCoachId
            ? `Select ${coaches.find((x) => x.id === aboutCoachId)?.name || "this instructor"}`
            : undefined
        }
        onPrimary={() => {
          if (!aboutCoachId) return;
          setSelectedCoachId(aboutCoachId);
          setSelectedSlot(null);
          setSelectedDate("");
          setSlots([]);
          setAboutCoachId(null);
        }}
      />
    </div>
  );
}
